import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Boxes, Search, Settings as Cog, Download, ChevronDown, ChevronRight, Mail, MapPin, X, Clock, Send, Eye, EyeOff, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, AlertTriangle, Link2, Copy, ExternalLink, ShieldCheck, BarChart3, Trash2, Ban, RotateCw, RotateCcw, FileText, Pencil, History } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import LedgerEditor from '../components/LedgerEditor';
import { WarehouseFilterSelect, useWarehouseFilter } from '../components/WarehouseFilterSelect';
import { apiErrorMessage } from '../lib/apiError';
import { StockAccessControl } from '../components/StockAccessControl';
import Rial from '../components/Rial';
import { formatDateTime, formatDate } from '../lib/dateFormat';

const COLUMNS = [
  { key: 'image',  label: 'Image',        defaultOn: false },
  { key: 'sku',    label: 'SKU',          defaultOn: false },
  { key: 'name',   label: 'Name',         defaultOn: true,  required: true },
  { key: 'pack',   label: 'Pack',         defaultOn: true  },
  { key: 'uom',    label: 'UOM',          defaultOn: false },
  { key: 'qty',    label: 'On hand',      defaultOn: false },
  { key: 'price',  label: 'Price',        defaultOn: false },
  // Feb 2026 — `status` is no longer a rendered column (OOS rows are
  // highlighted red instead). We keep the entry so legacy share-links
  // whose token still lists `status` decode cleanly, but it's fully
  // optional now — nothing forces it on.
  { key: 'status', label: 'Status',       defaultOn: false },
  { key: 'vat',    label: 'VAT',          defaultOn: false },
  { key: 'tag',    label: 'Tag',          defaultOn: false },
  { key: 'minqty', label: 'Min Qty',      defaultOn: false },
  // Best-before / expiry date — off by default so it only shows on
  // share links the admin explicitly opts into. Renders as a colour-
  // coded pill (green >90d, amber <90d, red <30d, dark-red EXPIRED).
  { key: 'expiry', label: 'Expiry',       defaultOn: false },
  // Feb 2026 — warehouse-side columns. Off by default; admins can
  // enable them from the "Show columns" chip menu just like the
  // other optional columns.
  //   • `hold`      — qty on placements flagged with `hold_pallet=true`
  //     (ASN received-and-held stock). Not deducted from `stock_qty`
  //     yet — signals what's physically present but embargoed.
  //   • `allocated` — qty currently committed to confirmed orders
  //     (sum of placement.allocated_qty across every pallet of the
  //     product). Reflects the same "held" number driver-dispatch,
  //     picking and stock reports all use.
  //   • `pallet`    — distinct pallet numbers the product lives on
  //     (e.g. "PLT-000001, PLT-000002" or "PLT-000001 +3" when the
  //     list exceeds 2 entries).
  //   • `location`  — distinct warehouse zone-location labels the
  //     product sits at (e.g. "A-01, B-05").
  { key: 'hold',      label: 'Hold',      defaultOn: false },
  { key: 'allocated', label: 'Allocated', defaultOn: false },
  { key: 'pallet',    label: 'PL #',      defaultOn: false },
  { key: 'location',  label: 'Loc',       defaultOn: false },
  // Feb 2026 — Zone chips (`GROUND`, `RACK`, etc.). Sourced from
  // `warehouseByProduct[pid].locations` — we split each location on
  // "-" and take the leading segment as the zone. Displayed as chips
  // so multi-zone products (e.g. same SKU stored on GROUND + RACK)
  // read at a glance.
  { key: 'zone',      label: 'Zone',      defaultOn: false },
  // Feb 2026 — Store column. Renders the human-readable warehouse
  // name(s) the product physically lives in. Sourced from
  // `warehouseByProduct[pid].warehouses` (populated by
  // `/admin/warehouses/placements-summary-by-product`). ON by default
  // so warehouse-scoped ops immediately see where each pallet lives.
  { key: 'store',     label: 'Store',     defaultOn: true },
];

const STATUSES = [
  { value: 'in_stock',     label: 'In stock',    color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'out_of_stock', label: 'Out of stock', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { value: 'coming_soon',  label: 'Coming soon', color: 'bg-amber-100 text-amber-700 border-amber-200' },
];

const useLS = (key, init) => {
  const [v, setV] = useState(() => {
    try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : init; }
    catch (e) { return init; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch (e) { /* ignore quota */ } }, [key, v]);
  return [v, setV];
};

const StatusPill = ({ value, onChange, disabled }) => {
  const s = STATUSES.find((x) => x.value === value) || STATUSES[0];
  const next = () => {
    const idx = STATUSES.findIndex((x) => x.value === value);
    onChange(STATUSES[(idx + 1) % STATUSES.length].value);
  };
  return (
    <button
      type="button"
      onClick={next}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${s.color} ${disabled ? 'opacity-50' : 'hover:shadow-sm'}`}
      title="Click to cycle status"
      data-testid="stock-status-pill"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {s.label.toUpperCase()}
    </button>
  );
};

const KpiCard = ({ label, value, accent, testId }) => (
  <div className={`flex-1 rounded-2xl border ${accent} p-4 bg-white`} data-testid={testId}>
    <div className="text-2xl font-extrabold text-neutral-900">{value}</div>
    <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mt-0.5">{label}</div>
  </div>
);

const AdminStock = () => {
  const { authHeaders, warehouses: myWarehouses, warehouseScopeAll } = useAdminAuth();
  const { can } = useAdminPermissions();
  const canStockUpload = can('products.stock_upload');
  const canStockDownload = can('products.stock_download');
  const canStockReport = can('products.stock_report');
  const canSku = can('products.field_sku') || can('products.sku_edit');
  const canQty = can('products.field_qty');
  const canPrice = can('products.field_price');
  const canStockValue = can('products.stock_value');
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // Super-admin gate for the Live Report Settings panel (ticker speed).
  // We fetch /admin/me once on mount; falls back to false on any error so
  // sub-admins never see the control even when /me is unreachable.
  const [isSuper, setIsSuper] = useState(false);
  // Feb 2026 — split Stock Ledger perms. `stock_ledger_view` (or edit,
  // or super, or wildcard) unlocks the History icon + Ledger modal.
  // `stock_ledger_edit` additionally unlocks the "Add manual entry"
  // form and per-row edit / delete controls inside the modal.
  const [ledgerCanView, setLedgerCanView] = useState(false);
  const [ledgerCanEdit, setLedgerCanEdit] = useState(false);
  // Manual ledger editor — product currently being edited.
  const [ledgerFor, setLedgerFor] = useState(null);
  React.useEffect(() => {
    let alive = true;
    axios.get(`${API}/admin/me`, { headers: authHeaders })
      .then((r) => {
        if (!alive) return;
        const superFlag = !!r.data?.is_super_admin;
        setIsSuper(superFlag);
        const perms = Array.isArray(r.data?.permissions) ? r.data.permissions : [];
        const wildcard = perms.includes('*') || perms.includes('warehouse');
        const canEdit = superFlag || wildcard || perms.includes('warehouse.stock_ledger_edit');
        const canView = canEdit || perms.includes('warehouse.stock_ledger_view');
        setLedgerCanView(canView);
        setLedgerCanEdit(canEdit);
      })
      .catch(() => {
        if (!alive) return;
        setIsSuper(false);
        setLedgerCanView(false);
        setLedgerCanEdit(false);
      });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Top-level page tabs. Only the super-admin sees Access Control because
  // managing IP allowlists is a sensitive op (not delegated to sub-admins
  // even when they hold stock permissions).
  const [activeTab, setActiveTab] = useState('stock');
  const [accessPending, setAccessPending] = useState(0);
  React.useEffect(() => {
    if (!isSuper) { setAccessPending(0); return; }
    let alive = true;
    const fetchCount = () => {
      axios.get(`${API}/admin/stock/access/pending-count`, { headers: authHeaders })
        .then(r => { if (alive) setAccessPending(Number(r.data?.count || 0)); })
        .catch(() => {});
    };
    fetchCount();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  // Super-admin share-link bypass: mint the signed `mr_stock_admin`
  // cookie on this browser so every live stock share link opens
  // instantly for the super admin — no sign-up / approval step.
  React.useEffect(() => {
    if (!isSuper) return;
    axios.post(`${API}/admin/stock/access/self-bypass`, {}, { headers: authHeaders }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  const [products, setProducts] = useState([]);
  // Feb 2026 — per-product warehouse aggregate (allocated_qty, pallet
  // list, location list). Keyed by product_id. Empty until `load()`
  // completes. Fetched lazily so admins who don't enable the new
  // Allocated / PL # / Loc columns pay no extra request cost.
  const [warehouseByProduct, setWarehouseByProduct] = useState({});
  // Feb 2026 — Top-of-page warehouse filter. Multi-site users can
  // flip between their assigned sites without leaving the page; the
  // selection is remembered in localStorage. Single-site users
  // never see the dropdown.
  const {
    warehouses: whList,
    value: whFilter,
    setValue: setWhFilter,
  } = useWarehouseFilter('stock');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [catFilter, setCatFilter] = useState(searchParams.get('cat') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  // Oct 2026 — Searchable Location filter. `locFilter` holds the
  // committed selection (empty string = no filter). `locQuery` is
  // the user's typed text inside the combobox popover; `locOpen`
  // toggles the popover. Values are compared case-insensitively +
  // whitespace/hyphen-insensitively (same normalisation used by the
  // main search box above) so a user pasting `CH1A1RA03` matches a
  // stored `CH1A1RA-03`.
  const [locFilter, setLocFilter] = useState(searchParams.get('loc') || '');
  const [locQuery, setLocQuery] = useState('');
  const [locOpen, setLocOpen] = useState(false);
  const locBoxRef = useRef(null);
  const [collapsed, setCollapsed] = useLS('adminStock.collapsedCats', {});
  const [showValue, setShowValue] = useLS('adminStock.showValue', true);
  // Feb 2026 — Store column sort. Cycles `none → asc → desc → none`
  // when the Store header is clicked. Sort is applied within each
  // category group by the first (alphabetically-earliest) warehouse
  // name the product physically lives in — rows with no placement
  // sink to the bottom.
  const [storeSort, setStoreSort] = useLS('adminStock.storeSort', 'none');
  const [visibleCols, setVisibleCols] = useLS(
    'adminStock.visibleCols',
    Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultOn]))
  );
  const [colsOpen, setColsOpen] = useState(false);

  // ---- Email Report modal state ----
  // The same payload backs both the immediate "Send Now" call and the
  // persisted weekly schedule. `loadSchedule` populates these from the
  // backend the first time the modal is opened so admins see the last
  // saved config without having to re-type recipients.
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [emailSignature, setEmailSignature] = useState('Warehouse Management');
  const [catRecipients, setCatRecipients] = useState({});  // { slug: "emails…" }
  const [alsoSendGlobal, setAlsoSendGlobal] = useState(true);
  const [catRecipExpanded, setCatRecipExpanded] = useState(false);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedFreq, setSchedFreq] = useState('weekly'); // 'daily' | 'weekly' (only used when enabled)
  const [schedDow, setSchedDow] = useState(1);   // 0=Mon … 6=Sun
  const [schedHour, setSchedHour] = useState(6); // UTC hour
  // Opt-in trigger: when on, every successful inventory upload also fires
  // a Stock Overview email to the same recipients configured above.
  // Independent of the Manual/Daily/Weekly schedule above so admins can
  // pair "no scheduled send" with "send when fresh stock arrives".
  const [schedAutoSendOnUpload, setSchedAutoSendOnUpload] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingSched, setSavingSched] = useState(false);
  const [schedLoaded, setSchedLoaded] = useState(false);

  // ---- Inventory bulk-upload state ----
  // Two-step flow: pick file → preview (dry-run from `commit=false`) → apply.
  const [invOpen, setInvOpen] = useState(false);
  // Feb 2026 — Stock Excel export column selector. Opens before the
  // download runs so admins can trim the sheet to the columns they
  // need. Backend `columns=` param filters; the on-sheet order stays
  // fixed so the file still round-trips through the uploader.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportCols, setExportCols] = useState({
    item_code: true,
    item_name: true,
    category: true,
    uom: true,
    pack: true,
    unit_price: true,
    vat: true,
    tag: true,
    min_qty: true,
    status: true,
    batch_ref: true,
    production_date: true,
    expiry_date: true,
    qty: true,
    ledger_drift: true,
    allocated: true,
    hold: true,
    loc: true,
    plt: true,
    warehouse: true,
  });
  const [exportBusy, setExportBusy] = useState(false);
  // Feb 2026 — collapse the per-pallet fan-out into one row per
  // product. Loc / Plt / Batch / Prod / Expiry / Warehouse render
  // blank; Qty / Allocated / Hold are summed across every placement.
  // Default false = keep the current per-pallet export.
  const [exportOneRowPerItem, setExportOneRowPerItem] = useState(false);
  const [invFile, setInvFile] = useState(null);
  const [invPreview, setInvPreview] = useState(null);  // server response from commit=false
  const [invUploading, setInvUploading] = useState(false);
  const [invApplying, setInvApplying] = useState(false);
  // Feb 2026 — Mandatory manual destination. The admin must pick a
  // warehouse before Apply is enabled — every placement produced by
  // this upload lands on that warehouse's STAGE (or the row's own
  // Loc/Plt inside that warehouse). Persists per browser in
  // localStorage so a returning admin sees their last pick.
  const [invWarehouseId, setInvWarehouseId] = useLS('adminStock.bulkUploadWarehouseId', '');
  // Feb 2026 perf pass — async-job progress state. `null` when idle,
  // `{ status, processed, total, persisted, error }` while an upload
  // is running. Powers the progress bar rendered inside the Apply
  // modal so admins see live count-of-N updates instead of a blocking
  // spinner.
  const [invJobProgress, setInvJobProgress] = useState(null);

  // ---- Shareable Stock Report (public signed URL) ----
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTtl, setShareTtl] = useState(30);   // days; 0 = no expiry
  const [shareIncludeLogo, setShareIncludeLogo] = useState(true);
  const [shareShowValue, setShareShowValue] = useState(true);
  const [shareCols, setShareCols] = useState({}); // {colKey: bool}
  // Human-readable identifier for the link, surfaced in the records audit
  // panel (e.g. "Glace Distributors – Mubarak"). Optional but strongly
  // encouraged so admins can answer "which link is for whom" at a glance.
  const [shareLabel, setShareLabel] = useState('');
  const [shareMode, setShareMode] = useState('single'); // 'single' | 'multi' | 'per_category'
  const [shareSelectedCats, setShareSelectedCats] = useState({}); // {slug: bool} for multi mode
  // Feb 2026 — Warehouse scope for the share link. Empty = all warehouses.
  const [shareSelectedWhs, setShareSelectedWhs] = useState({}); // {warehouse_id: bool}
  const [shareWarehouses, setShareWarehouses] = useState([]);   // [{id,name}, ...]
  const [shareUrl, setShareUrl] = useState('');
  const [shareShortUrl, setShareShortUrl] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareGenerating, setShareGenerating] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareCopiedSlug, setShareCopiedSlug] = useState('');
  const [shareShowLong, setShareShowLong] = useState(false);
  // ---- Generated URL records (history) ----
  // Surfaces every share URL ever minted so admins can audit, revoke
  // (soft-kill so /api/s/<slug> starts returning 410), or hard-delete.
  const [shareRecords, setShareRecords] = useState([]);
  const [shareRecordsLoading, setShareRecordsLoading] = useState(false);
  const [shareRecordsStatusFilter, setShareRecordsStatusFilter] = useState('all'); // 'all'|'active'|'revoked'|'expired'
  const [shareRecordBusySlug, setShareRecordBusySlug] = useState(''); // disables row buttons while revoke/delete is in flight
  const loadShareRecords = React.useCallback(async () => {
    setShareRecordsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stock/share-links/list`, {
        headers: authHeaders, params: { status: 'all', limit: 200 },
      });
      setShareRecords(Array.isArray(data?.links) ? data.links : []);
    } catch (err) {
      // Non-fatal — the records panel just stays empty.
      setShareRecords([]);
    } finally {
      setShareRecordsLoading(false);
    }
  }, [authHeaders]);
  // Feb 2026 — Fetch the warehouses list once so the modal's
  // "Warehouses" multi-select can render them. Best-effort; on error
  // the block simply hides (the link still mints as "all warehouses").
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/warehouses`, { headers: authHeaders });
        if (cancelled) return;
        const list = Array.isArray(data?.warehouses) ? data.warehouses : [];
        setShareWarehouses(list.map((w) => ({ id: w.id, name: w.name || w.id })));
      } catch { /* soft-fail */ }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);
  const [shareRecordEditingSlug, setShareRecordEditingSlug] = useState('');
  const [shareRecordEditValue, setShareRecordEditValue] = useState('');
  // Separate editing state for the slug rename flow so it doesn't
  // fight with the label-editor state — same row can only be in one
  // edit mode at a time.
  const [shareRecordEditingSlugFor, setShareRecordEditingSlugFor] = useState('');
  const [shareRecordSlugValue, setShareRecordSlugValue] = useState('');
  const saveShareRecordLabel = async (slug, newLabel) => {
    setShareRecordBusySlug(slug);
    try {
      await axios.patch(
        `${API}/admin/stock/share-links/${slug}`,
        { label: (newLabel || '').trim() || null },
        { headers: authHeaders },
      );
      toast({ title: 'Label saved' });
      setShareRecordEditingSlug('');
      setShareRecordEditValue('');
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not save label', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  // Rename the slug (short URL suffix) — e.g. `aB3xLp09Kf` → `warehouse-team`.
  // Backend preserves the token, expiry and revocation state, so the LONG
  // signed URL keeps working and only the short URL changes.
  const saveShareRecordSlug = async (oldSlug, newSlug) => {
    const trimmed = (newSlug || '').trim();
    if (!trimmed) {
      toast({ title: 'Slug required', description: 'Type a name for the URL suffix.' });
      return;
    }
    if (trimmed === oldSlug) {
      setShareRecordEditingSlugFor('');
      setShareRecordSlugValue('');
      return;
    }
    if (!/^[A-Za-z0-9_-]{3,60}$/.test(trimmed)) {
      toast({
        title: 'Invalid URL',
        description: '3-60 chars, letters/digits/`-`/`_` only.',
      });
      return;
    }
    setShareRecordBusySlug(oldSlug);
    try {
      await axios.patch(
        `${API}/admin/stock/share-links/${oldSlug}`,
        { slug: trimmed },
        { headers: authHeaders },
      );
      toast({ title: 'URL renamed', description: `/api/s/${trimmed}` });
      setShareRecordEditingSlugFor('');
      setShareRecordSlugValue('');
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not rename', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  // Edit the columns baked into an existing share link — re-signs the
  // underlying token so the SAME slug now serves a different view.
  const [shareRecordColumnsModalFor, setShareRecordColumnsModalFor] = useState('');
  const [shareRecordColumnsDraft, setShareRecordColumnsDraft] = useState({});
  const openShareRecordColumns = (r) => {
    // Pre-fill the modal with whatever columns are currently baked into
    // this link — falls back to sensible defaults on legacy links whose
    // tokens the backend couldn't decode.
    const current = Array.isArray(r.columns) && r.columns.length ? r.columns : ['name'];
    const draft = {};
    COLUMNS.forEach((c) => { draft[c.key] = current.includes(c.key); });
    // Only `name` is required — force it on so save can't produce
    // an unusable view. Status is now optional (report hides it).
    draft.name = true;
    setShareRecordColumnsDraft(draft);
    setShareRecordColumnsModalFor(r.slug);
  };
  const saveShareRecordColumns = async () => {
    const slug = shareRecordColumnsModalFor;
    if (!slug) return;
    const cols = COLUMNS.filter((c) => shareRecordColumnsDraft[c.key]).map((c) => c.key);
    if (!cols.length) {
      toast({ title: 'Pick at least one column' });
      return;
    }
    setShareRecordBusySlug(slug);
    try {
      await axios.patch(
        `${API}/admin/stock/share-links/${slug}`,
        { columns: cols },
        { headers: authHeaders },
      );
      toast({ title: 'Columns updated', description: `${cols.length} column${cols.length === 1 ? '' : 's'} visible now.` });
      setShareRecordColumnsModalFor('');
      setShareRecordColumnsDraft({});
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not save columns', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  // Edit the CATEGORIES baked into an existing share link — re-signs the
  // underlying token so the same slug now shows a different category set
  // on the public report. Empty selection = all categories.
  const [shareRecordCatsModalFor, setShareRecordCatsModalFor] = useState('');
  const [shareRecordCatsDraft, setShareRecordCatsDraft] = useState({});
  const [shareRecordCatsAll, setShareRecordCatsAll] = useState(false);
  const [shareRecordStatusDraft, setShareRecordStatusDraft] = useState('');
  const openShareRecordCats = (r) => {
    const current = Array.isArray(r.category_slugs) ? r.category_slugs : [];
    const draft = {};
    (categories || []).forEach((c) => { draft[c.slug] = current.includes(c.slug); });
    setShareRecordCatsDraft(draft);
    setShareRecordCatsAll(current.length === 0);
    setShareRecordStatusDraft(r.status_filter || '');
    setShareRecordCatsModalFor(r.slug);
  };
  const saveShareRecordCats = async () => {
    const slug = shareRecordCatsModalFor;
    if (!slug) return;
    const slugs = shareRecordCatsAll
      ? []
      : (categories || []).filter((c) => shareRecordCatsDraft[c.slug]).map((c) => c.slug);
    if (!shareRecordCatsAll && !slugs.length) {
      toast({ title: 'Pick at least one division', description: 'Or switch on "All divisions".' });
      return;
    }
    setShareRecordBusySlug(slug);
    try {
      await axios.patch(
        `${API}/admin/stock/share-links/${slug}`,
        { category_slugs: slugs, status_filter: shareRecordStatusDraft },
        { headers: authHeaders },
      );
      toast({
        title: 'Divisions updated',
        description: shareRecordCatsAll ? 'This link now shows ALL categories.' : `${slugs.length} categor${slugs.length === 1 ? 'y' : 'ies'} visible on this link now.`,
      });
      setShareRecordCatsModalFor('');
      setShareRecordCatsDraft({});
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not save divisions', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  // Extend the expiry of an existing share link — re-signs the token
  // with a fresh `exp` so the short URL keeps working with a new TTL.
  const [shareRecordExtendMenuFor, setShareRecordExtendMenuFor] = useState('');
  const extendShareRecordExpiry = async (slug, days) => {
    setShareRecordBusySlug(slug);
    try {
      const { data } = await axios.patch(
        `${API}/admin/stock/share-links/${slug}`,
        { extend_days: days },
        { headers: authHeaders },
      );
      const expIso = data?.link?.expires_at;
      const nice = expIso ? formatDate(expIso) : `+${days}d`;
      toast({ title: `Expiry extended (+${days}d)`, description: `New expiry: ${nice}` });
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not extend', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  const revokeShareRecord = async (slug) => {
    if (!slug) return;
    if (!window.confirm('Revoke this URL? Anyone with the link will lose access immediately (both short URL and the long signed URL).')) return;
    setShareRecordBusySlug(slug);
    try {
      await axios.post(`${API}/admin/stock/share-links/${slug}/revoke`, null, { headers: authHeaders });
      toast({ title: 'URL revoked', description: 'Short link and long signed URL are both blocked.' });
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not revoke', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  const restoreShareRecord = async (slug) => {
    if (!slug) return;
    setShareRecordBusySlug(slug);
    try {
      await axios.post(`${API}/admin/stock/share-links/${slug}/restore`, null, { headers: authHeaders });
      toast({ title: 'URL restored', description: 'Both URLs are active again.' });
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not restore', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  const deleteShareRecord = async (slug) => {
    if (!slug) return;
    if (!window.confirm('Delete this URL record permanently? The audit trail will be lost.')) return;
    setShareRecordBusySlug(slug);
    try {
      await axios.delete(`${API}/admin/stock/share-links/${slug}`, { headers: authHeaders });
      toast({ title: 'URL deleted' });
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not delete', description: apiErrorMessage(err) });
    } finally {
      setShareRecordBusySlug('');
    }
  };
  // Ticker speed (super-admin only) — fetched from /admin/settings/live-report
  const [tickerSpeed, setTickerSpeed] = useState(30);
  const [tickerEnabled, setTickerEnabled] = useState(true);
  // Maintenance mode for the live stock report (super-admin only).
  // When `liveDisabled=true`, every visitor sees the maintenance screen
  // — even approved IPs.
  const [liveDisabled, setLiveDisabled] = useState(false);
  const [liveMsg, setLiveMsg] = useState('');
  // Daily auto-pause window (Oman time) — when enabled, the report
  // automatically shows maintenance during the configured HH:MM range
  // even if `liveDisabled` is false.
  const [autoPauseEnabled, setAutoPauseEnabled] = useState(false);
  const [autoPauseStart, setAutoPauseStart] = useState('23:00');
  const [autoPauseEnd, setAutoPauseEnd] = useState('06:00');
  // Per-item ledger window on the live report (days of in/out history
  // shown when a viewer expands a product row). One of 10/15/20/30.
  const [ledgerDays, setLedgerDays] = useState(15);
  const [tickerSaving, setTickerSaving] = useState(false);
  // Guard flag flipped on while the admin is actively dragging the
  // ticker-speed range slider (mouse down / touch start / arrow key)
  // and cleared on release. Prevents `loadTickerSettings` or the
  // `saveTickerSettings` PUT response from snapping the thumb back
  // mid-drag. `useRef` (not state) so mutations don't trigger renders
  // and fight the browser's own drag handling.
  const isAdjustingSpeedRef = useRef(false);
  // Lazily load the saved value when modal opens (and super) so we don't
  // hit the endpoint on every page render.
  const loadTickerSettings = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings/live-report`, { headers: authHeaders });
      // Don't stomp the thumb if the admin is mid-drag when the fetch
      // resolves (slow network / backgrounded tab). All other settings
      // are checkbox / time / string values and always safe to hydrate.
      if (!isAdjustingSpeedRef.current) {
        setTickerSpeed(Number(data?.ticker_speed_seconds ?? 30));
      }
      setTickerEnabled(!!data?.ticker_enabled);
      setLiveDisabled(!!data?.disabled);
      setLiveMsg(String(data?.maintenance_message || ''));
      setAutoPauseEnabled(!!data?.auto_pause_enabled);
      setAutoPauseStart(String(data?.auto_pause_start || '23:00'));
      setAutoPauseEnd(String(data?.auto_pause_end || '06:00'));
      setLedgerDays([10, 15, 20, 30].includes(Number(data?.ledger_days)) ? Number(data.ledger_days) : 15);
    } catch { /* non-critical — leave defaults */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const saveTickerSettings = async (overrides = {}) => {
    setTickerSaving(true);
    try {
      const body = {
        ticker_enabled: 'ticker_enabled' in overrides ? !!overrides.ticker_enabled : tickerEnabled,
        ticker_speed_seconds: Math.max(10, Math.min(600, Number(overrides.ticker_speed_seconds ?? tickerSpeed))),
        disabled: 'disabled' in overrides ? !!overrides.disabled : liveDisabled,
        maintenance_message: 'maintenance_message' in overrides ? String(overrides.maintenance_message || '') : liveMsg,
        auto_pause_enabled: 'auto_pause_enabled' in overrides ? !!overrides.auto_pause_enabled : autoPauseEnabled,
        auto_pause_start: 'auto_pause_start' in overrides ? String(overrides.auto_pause_start || '23:00') : autoPauseStart,
        auto_pause_end: 'auto_pause_end' in overrides ? String(overrides.auto_pause_end || '06:00') : autoPauseEnd,
        ledger_days: 'ledger_days' in overrides ? Number(overrides.ledger_days) : ledgerDays,
      };
      await axios.put(`${API}/admin/settings/live-report`, body, { headers: authHeaders });
      // Guard the ticker-speed echo only — the admin could have already
      // started a fresh drag while the PUT was in flight, and echoing
      // back the just-saved value would snap the thumb backwards.
      if (!isAdjustingSpeedRef.current) {
        setTickerSpeed(body.ticker_speed_seconds);
      }
      setTickerEnabled(body.ticker_enabled);
      setLiveDisabled(body.disabled);
      setLiveMsg(body.maintenance_message);
      setAutoPauseEnabled(body.auto_pause_enabled);
      setAutoPauseStart(body.auto_pause_start);
      setAutoPauseEnd(body.auto_pause_end);
      setLedgerDays(body.ledger_days);
      toast({ title: 'Live-report settings saved', description: body.disabled ? 'Live report is PAUSED — visitors see the maintenance page.' : `Scroll cycle: ${body.ticker_speed_seconds}s` });
    } catch (err) {
      toast({ title: 'Could not save', description: apiErrorMessage(err) });
    } finally {
      setTickerSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, me, wh] = await Promise.all([
        // Authenticated endpoint — backend filters by the admin's
        // `category_slugs` scope so sub-admins only see products in
        // categories they're authorised for.
        axios.get(`${API}/admin/products`, { headers: authHeaders }),
        // Admin-scoped categories endpoint — returns ALL categories
        // including ones hidden from the public storefront
        // (`is_visible: false`). The public `/categories` endpoint
        // would silently drop those, leaving admins unable to manage
        // stock for "internal-only" categories (e.g. export SKUs that
        // shouldn't appear on the retail catalog).
        axios.get(`${API}/admin/categories`, { headers: authHeaders }),
        // Pull admin profile so we can scope categories by `category_slugs`
        // (sub-admins) instead of by which categories happen to have stock.
        // Empty categories must remain visible so admins can manage share
        // links, recipients and settings for them before any product is added.
        axios.get(`${API}/admin/me`, { headers: authHeaders }).catch(() => null),
        // Per-product warehouse aggregate — powers the Allocated / PL # /
        // Loc columns. Silently no-op on error so a warehouse outage
        // doesn't take down the stock report.
        axios.get(
          `${API}/admin/warehouses/placements-summary-by-product`,
          {
            headers: authHeaders,
            params: whFilter ? { warehouse_id: whFilter } : {},
          },
        ).catch(() => null),
      ]);
      const prods = p.data || [];
      setProducts(prods);
      const cats = c.data || [];
      // Scope categories to what this admin is allowed to manage:
      //   • super admin OR sub-admin with empty `category_slugs` → all
      //   • scoped sub-admin → intersect with their allowed slugs
      // This keeps empty (just-created) categories visible so admins can
      // wire up share links, recipients and settings before adding products.
      const meData = me?.data || null;
      const isSuper = !!meData?.is_super_admin;
      const scopeSlugs = Array.isArray(meData?.category_slugs) ? meData.category_slugs : [];
      let visibleCats = cats;
      if (!isSuper && scopeSlugs.length > 0) {
        const allowed = new Set(scopeSlugs);
        visibleCats = cats.filter((cat) => allowed.has(cat.slug));
      }
      setCategories(visibleCats);
      setWarehouseByProduct((wh && wh.data && wh.data.products) || {});
    } catch (err) {
      toast({ title: 'Failed to load', description: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  // Refetch the per-product warehouse aggregate when the filter
  // changes. Products / categories don't depend on the filter so a
  // full re-load would be wasteful — call `load()` because the
  // aggregate is fetched inside it, and load() is cheap after the
  // categories cache has warmed.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [whFilter]);

  // URL sync (one-way: state → URL)
  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    if (catFilter !== 'all') next.cat = catFilter;
    if (statusFilter !== 'all') next.status = statusFilter;
    if (locFilter) next.loc = locFilter;
    setSearchParams(next, { replace: true });
  }, [search, catFilter, statusFilter, locFilter, setSearchParams]);

  const setStatus = async (product, next) => {
    setUpdating((m) => ({ ...m, [product.id]: true }));
    try {
      await axios.put(`${API}/admin/products/${product.id}`, { stock_status: next }, { headers: authHeaders });
      setProducts((list) => list.map((p) => p.id === product.id ? { ...p, stock_status: next, in_stock: next === 'in_stock' } : p));
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    } finally {
      setUpdating((m) => { const n = { ...m }; delete n[product.id]; return n; });
    }
  };

  const statusOf = (p) => {
    // 1. Admin's explicit "Coming soon" wins — never auto-override.
    if (p.stock_status === 'coming_soon') return 'coming_soon';
    // 2. Admin's explicit "Out of stock" wins (even when qty > 0).
    if (p.stock_status === 'out_of_stock') return 'out_of_stock';
    // 3. Quantity-driven: 0 on-hand = out of stock regardless of any
    //    stale `in_stock` boolean from a pre-inventory-upload data import.
    //
    // Feb 2026 (user directive) — the on-screen "On Hand" quantity is
    // sourced from the PHYSICAL rack total (`placements_sum_qty`) so
    // it matches the Excel export exactly. Falls back to the legacy
    // ledger total (`stock_qty`) only when the physical field is
    // missing (older API payloads that pre-date this rollout).
    const qty = Number(p.placements_sum_qty ?? p.stock_qty ?? -1);
    if (qty === 0) return 'out_of_stock';
    // 4. Honor explicit status, else legacy `in_stock` boolean.
    return p.stock_status || (p.in_stock === false ? 'out_of_stock' : 'in_stock');
  };

  // Feb 2026 (user directive) — single authoritative helper for the
  // Stock Report's on-screen "On Hand" number. Everything visible on
  // this page (row cell, KPI counts, group counters, stock value)
  // goes through this so nothing drifts against the Excel export.
  const onHandOf = (p) =>
    Number.isFinite(p?.placements_sum_qty)
      ? Number(p.placements_sum_qty)
      : Number(p?.stock_qty ?? 0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Feb 2026 — normalised form strips spaces + hyphens so a user
    // pasting "CH1A1RA03" matches a stored "CH1A1RA-03" (backend
    // renders locations as "{zone}-{location_id}").
    const qNorm = q.replace(/[\s-]+/g, '');
    // Oct 2026 — Location filter uses the same normalisation so
    // comparing against stored `bucket.locations[]` is consistent
    // with the main search box.
    const locNorm = locFilter ? String(locFilter).toLowerCase().replace(/[\s-]+/g, '') : '';
    return products.filter((p) => {
      if (catFilter !== 'all' && p.category_slug !== catFilter) return false;
      if (statusFilter !== 'all' && statusOf(p) !== statusFilter) return false;
      // Location filter: keep rows that physically sit at least once
      // on the selected location. Products with no placements fall
      // out automatically (empty `locations` array ⇒ no match).
      if (locNorm) {
        const bucket = warehouseByProduct[p.id] || {};
        const locs = Array.isArray(bucket.locations) ? bucket.locations : [];
        const hit = locs.some((v) => String(v || '').toLowerCase().replace(/[\s-]+/g, '') === locNorm);
        if (!hit) return false;
      }
      if (q) {
        // Expanded search: name, size, sku, PLUS every pallet number
        // and location code held by this product's warehouse-placements
        // bucket. Lets ops paste a pallet id (e.g. "FA15181") or a
        // slot code (e.g. "CH1A1RA03") into the main search box and
        // zoom straight to the product holding it.
        // Feb 2026 — Store column search: also match on warehouse
        // names so typing "NG" surfaces every product currently
        // physically sitting in the NG warehouse.
        const bucket = warehouseByProduct[p.id] || {};
        const pallets = Array.isArray(bucket.pallets) ? bucket.pallets : [];
        const locations = Array.isArray(bucket.locations) ? bucket.locations : [];
        const warehouses = Array.isArray(bucket.warehouses) ? bucket.warehouses : [];
        const hay = [
          p.name, p.size, p.sku,
          ...pallets,
          ...locations,
          ...warehouses,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        const hit = hay.some((v) =>
          v.includes(q) || (qNorm && v.replace(/[\s-]+/g, '').includes(qNorm))
        );
        if (!hit) return false;
      }
      return true;
    });
  }, [products, search, catFilter, statusFilter, locFilter, warehouseByProduct]);

  // Oct 2026 — Distinct location catalogue. Flattens every
  // `warehouseByProduct[*].locations` array into a Set so the
  // searchable filter popover can list every physical slot the
  // storefront knows about (scoped to the current warehouse filter
  // because the summary endpoint is already scoped by `whFilter`).
  // Sorted alphanumerically with a natural fallback so `CH1A1RA02`
  // comes before `CH1A1RA10`.
  const allLocations = useMemo(() => {
    const set = new Set();
    Object.values(warehouseByProduct || {}).forEach((bucket) => {
      (bucket?.locations || []).forEach((l) => {
        const v = String(l || '').trim();
        if (v) set.add(v);
      });
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [warehouseByProduct]);

  const locationMatches = useMemo(() => {
    const q = locQuery.trim().toLowerCase().replace(/[\s-]+/g, '');
    if (!q) return allLocations.slice(0, 100);
    const hits = allLocations.filter((l) =>
      l.toLowerCase().replace(/[\s-]+/g, '').includes(q)
    );
    return hits.slice(0, 100);
  }, [allLocations, locQuery]);

  // Dismiss the Location popover when the user clicks outside the
  // combobox so clicking a table row doesn't leave a stale dropdown
  // hanging over the grid.
  useEffect(() => {
    if (!locOpen) return undefined;
    const onDown = (e) => {
      if (locBoxRef.current && !locBoxRef.current.contains(e.target)) {
        setLocOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [locOpen]);

  const groups = useMemo(() => {
    const byCat = {};
    filtered.forEach((p) => {
      const slug = p.category_slug || 'uncategorised';
      (byCat[slug] = byCat[slug] || []).push(p);
    });
    // Only suppress empty categories when a row-level filter (search or
    // status) would have hidden their products anyway. Otherwise show
    // empty categories with a "No products yet" placeholder so admins can
    // see newly-created categories at a glance and still manage settings
    // / share links for them. Respect catFilter: if a single category is
    // chosen, surface it even when empty.
    const rowFilterActive = !!search.trim() || statusFilter !== 'all';
    return categories
      .filter((c) => {
        const hasItems = byCat[c.slug] && byCat[c.slug].length;
        if (hasItems) return true;
        if (rowFilterActive) return false;
        if (catFilter === 'all' || catFilter === c.slug) return true;
        return false;
      })
      .map((c) => {
        const items = byCat[c.slug] || [];
        if (items.length < 2) {
          return { ...c, items };
        }
        if (storeSort === 'none') {
          // Feb 2026 — Default sort: alphabetical by Item Code (SKU),
          // case-insensitive. Rows without an SKU drop to the bottom.
          // Keeps parity with the Live Stock Report backend render.
          const sorted = [...items].sort((a, b) => {
            const sa = (a.sku || '').trim().toUpperCase();
            const sb = (b.sku || '').trim().toUpperCase();
            if (!sa && !sb) return 0;
            if (!sa) return 1;
            if (!sb) return -1;
            if (sa < sb) return -1;
            if (sa > sb) return 1;
            return 0;
          });
          return { ...c, items: sorted };
        }
        // Feb 2026 — Store column sort: order rows by the FIRST
        // warehouse the product lives in. Empty-placement rows sink
        // to the bottom in both asc + desc so they don't hide real
        // stock at the top.
        const dir = storeSort === 'asc' ? 1 : -1;
        const key = (p) => {
          const list = (warehouseByProduct[p.id] || {}).warehouses || [];
          const first = (list[0] || '').toLowerCase();
          return first;
        };
        const sorted = [...items].sort((a, b) => {
          const ka = key(a); const kb = key(b);
          if (!ka && !kb) return 0;
          if (!ka) return 1;   // empty always at bottom
          if (!kb) return -1;
          if (ka < kb) return -1 * dir;
          if (ka > kb) return 1 * dir;
          return 0;
        });
        return { ...c, items: sorted };
      });
  }, [filtered, categories, search, statusFilter, catFilter, storeSort, warehouseByProduct]);

  const kpis = useMemo(() => {
    const k = { total: products.length, in_stock: 0, out_of_stock: 0, coming_soon: 0, value: 0 };
    products.forEach((p) => {
      const s = statusOf(p);
      k[s] = (k[s] || 0) + 1;
      if (s === 'in_stock') k.value += Number(p.price || 0) * onHandOf(p);
    });
    return k;
  }, [products]);

  const openEmailModal = async () => {
    setEmailOpen(true);
    if (schedLoaded) return;
    try {
      const { data } = await axios.get(`${API}/admin/stock/schedule`, { headers: authHeaders });
      setEmailRecipients(data.recipients_text || '');
      setEmailSubject(data.subject || '');
      setEmailNote(data.note || '');
      setSchedEnabled(!!data.enabled);
      if (typeof data.include_logo === 'boolean') setIncludeLogo(data.include_logo);
      if (typeof data.signature === 'string') setEmailSignature(data.signature || 'Warehouse Management');
      if (data.category_recipients && typeof data.category_recipients === 'object') setCatRecipients(data.category_recipients);
      if (typeof data.also_send_global === 'boolean') setAlsoSendGlobal(data.also_send_global);
      if (data.frequency === 'daily' || data.frequency === 'weekly') setSchedFreq(data.frequency);
      if (typeof data.day_of_week === 'number') setSchedDow(data.day_of_week);
      if (typeof data.hour_utc === 'number') setSchedHour(data.hour_utc);
      if (typeof data.auto_send_on_upload === 'boolean') setSchedAutoSendOnUpload(data.auto_send_on_upload);
      setSchedLoaded(true);
    } catch (err) {
      toast({ title: 'Could not load saved schedule', description: apiErrorMessage(err) });
    }
  };

  const buildEmailPayload = () => ({
    recipients_text: emailRecipients,
    subject: emailSubject || undefined,
    note: emailNote,
    include_logo: includeLogo,
    show_stock_value: showValue,
    signature: emailSignature,
    category_recipients: catRecipients,
    also_send_global: alsoSendGlobal,
    columns: COLUMNS.filter((c) => visibleCols[c.key]).map((c) => c.key),
    category_slug: catFilter === 'all' ? null : catFilter,
    status_filter: statusFilter === 'all' ? null : statusFilter,
  });

  const sendEmailNow = async () => {
    const anyCatRecipients = Object.values(catRecipients).some((v) => (v || '').trim());
    const hasGlobal = !!emailRecipients.trim() && alsoSendGlobal;
    if (!hasGlobal && !anyCatRecipients) {
      toast({ title: 'Add recipients', description: 'Add the global list (and enable the toggle) or fill at least one per-category list.' });
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/stock/email`, buildEmailPayload(), { headers: authHeaders });
      toast({
        title: 'Report sent',
        description: `${data.sent} sent · ${data.failed || 0} failed${data.breakdown && data.breakdown.length > 1 ? ` (${data.breakdown.length} emails)` : ''}`,
      });
    } catch (err) {
      toast({ title: 'Send failed', description: apiErrorMessage(err) });
    } finally {
      setSending(false);
    }
  };

  // Weekly Insights digest — analytical email with charts + AI narrative.
  // Reuses the same recipients_text the admin already configured for the
  // regular stock report (so they manage ONE list across both surfaces).
  // Slow request (10-30s) due to chart rendering + LLM call, so we show
  // a distinct loading state on the button.
  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigestNow = async () => {
    const recips = emailRecipients.trim();
    if (!recips) {
      toast({ title: 'Add recipients first', description: 'Fill the global recipients list above so the digest knows where to send.' });
      return;
    }
    setSendingDigest(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/stock/digest/send`,
        { recipients_text: recips },
        { headers: authHeaders, timeout: 60000 }
      );
      toast({
        title: 'Weekly digest sent',
        description: `${data.sent} sent · ${data.failed || 0} failed · ${data.week}${data.narrative_chars ? ' · AI summary included' : ''}`,
      });
    } catch (err) {
      toast({ title: 'Digest send failed', description: apiErrorMessage(err) });
    } finally {
      setSendingDigest(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      await axios.post(`${API}/admin/stock/schedule`, {
        enabled: schedEnabled,
        frequency: schedFreq,
        day_of_week: Number(schedDow),
        hour_utc: Number(schedHour),
        recipients_text: emailRecipients,
        subject: emailSubject || undefined,
        note: emailNote,
        include_logo: includeLogo,
        show_stock_value: showValue,
        signature: emailSignature,
        category_recipients: catRecipients,
        also_send_global: alsoSendGlobal,
        columns: COLUMNS.filter((c) => visibleCols[c.key]).map((c) => c.key),
        category_slug: catFilter === 'all' ? null : catFilter,
        status_filter: statusFilter === 'all' ? null : statusFilter,
        auto_send_on_upload: schedAutoSendOnUpload,
      }, { headers: authHeaders });
      const label = !schedEnabled ? 'Auto-send disabled' : (schedFreq === 'daily' ? 'Daily schedule saved' : 'Weekly schedule saved');
      toast({ title: label });
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    } finally {
      setSavingSched(false);
    }
  };

  // Lightweight "save the recipients only" handler — persists whichever
  // subset of the email form the admin just edited (global list OR
  // category map OR both) without flipping the schedule on or sending
  // anything. Reuses the same /admin/stock/schedule endpoint because
  // recipients_text + category_recipients live on the same schedule
  // document; passing the current `enabled` value keeps the cron in
  // whatever state the admin already chose.
  //
  // Two button targets share this handler:
  //   • "Save recipients" next to the global list   (scope='global')
  //   • "Save category lists" inside the accordion  (scope='categories')
  // The scope drives the toast copy so the admin gets a precise
  // confirmation of what just landed.
  const [savingRecipients, setSavingRecipients] = useState(false);
  const saveRecipientsOnly = async (scope) => {
    setSavingRecipients(true);
    try {
      await axios.post(`${API}/admin/stock/schedule`, {
        enabled: schedEnabled,
        frequency: schedFreq,
        day_of_week: Number(schedDow),
        hour_utc: Number(schedHour),
        recipients_text: emailRecipients,
        subject: emailSubject || undefined,
        note: emailNote,
        include_logo: includeLogo,
        show_stock_value: showValue,
        signature: emailSignature,
        category_recipients: catRecipients,
        also_send_global: alsoSendGlobal,
        columns: COLUMNS.filter((c) => visibleCols[c.key]).map((c) => c.key),
        category_slug: catFilter === 'all' ? null : catFilter,
        status_filter: statusFilter === 'all' ? null : statusFilter,
        auto_send_on_upload: schedAutoSendOnUpload,
      }, { headers: authHeaders });
      const titles = {
        global: 'Recipients saved',
        categories: 'Per-category lists saved',
      };
      const descriptions = {
        global: 'The global list will receive the next manual / scheduled / auto-on-upload send.',
        categories: `${Object.values(catRecipients).filter((v) => (v || '').trim()).length} category lists saved — each gets its own filtered email on the next send.`,
      };
      toast({
        title: titles[scope] || 'Saved',
        description: descriptions[scope] || '',
      });
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err) });
    } finally {
      setSavingRecipients(false);
    }
  };

  // ---- Inventory upload handlers ----
  // Feb 2026 — Fixed on-sheet order for the export column-selector modal.
  // Keys match `_STOCK_EXPORT_COLUMNS` on the backend; labels match the
  // .xlsx headers so admins recognise what each toggle produces.
  const STOCK_EXPORT_COLUMNS = [
    { key: 'item_code',       label: 'Item Code *' },
    { key: 'item_name',       label: 'Item Name' },
    { key: 'category',        label: 'Category' },
    { key: 'uom',             label: 'UOM' },
    { key: 'pack',            label: 'Pack' },
    { key: 'unit_price',      label: 'Unit Price' },
    { key: 'vat',             label: 'VAT' },
    { key: 'tag',             label: 'Tag' },
    { key: 'min_qty',         label: 'Min Qty' },
    { key: 'status',          label: 'Status' },
    { key: 'batch_ref',       label: 'Batch Ref' },
    { key: 'production_date', label: 'Production Date' },
    { key: 'expiry_date',     label: 'Expiry Date' },
    { key: 'qty',             label: 'On Hand' },
    { key: 'ledger_drift',    label: 'Ledger Drift' },
    { key: 'allocated',       label: 'Allocated' },
    { key: 'hold',            label: 'Hold' },
    { key: 'loc',             label: 'Loc' },
    { key: 'plt',             label: 'Plt' },
    { key: 'warehouse',       label: 'Warehouse' },
  ];
  const openExportModal = () => setExportOpen(true);
  const downloadInvExport = async () => {
    const selected = STOCK_EXPORT_COLUMNS.map((c) => c.key).filter((k) => exportCols[k]);
    if (selected.length === 0) {
      toast({ title: 'Pick at least one column', description: 'Tick at least one column before downloading.' });
      return;
    }
    // Feb 2026 — send the CURRENT filtered product-id list so the sheet
    // matches what's on screen (Category / Search / Status filters).
    // POST (not GET) so a large id list doesn't run into URL-length caps.
    const productIds = (filtered || []).map((p) => p.id).filter(Boolean);
    if (productIds.length === 0) {
      toast({ title: 'Nothing to export', description: 'The current filter matches zero products. Clear the filter and try again.' });
      return;
    }
    setExportBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/products/inventory-export`,
        {
          columns: selected.join(','),
          product_ids: productIds,
          one_row_per_item: exportOneRowPerItem,
        },
        { headers: authHeaders, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (err) {
      toast({ title: 'Stock export failed', description: apiErrorMessage(err) });
    } finally {
      setExportBusy(false);
    }
  };

  const downloadInvTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/products/inventory-template`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Template download failed', description: apiErrorMessage(err) });
    }
  };

  const previewInventory = async (file) => {
    setInvUploading(true);
    setInvPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/products/inventory-upload?commit=false`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setInvPreview(data);
    } catch (err) {
      toast({ title: 'Could not read file', description: apiErrorMessage(err) });
    } finally {
      setInvUploading(false);
    }
  };

  const applyInventory = async () => {
    if (!invFile) return;
    // Feb 2026 — Strict placement: bail out early with a clear toast
    // instead of firing a request that the backend would 400 on.
    if (!invWarehouseId) {
      toast({
        title: 'Pick a destination warehouse',
        description: 'Select a warehouse before applying the upload. Stock lands only on the warehouse you choose here.',
      });
      return;
    }
    setInvApplying(true);
    setInvJobProgress(null);
    try {
      const fd = new FormData();
      fd.append('file', invFile);
      fd.append('warehouse_id', invWarehouseId);
      // Feb 2026 perf pass — use the async-job flow so the request
      // returns in milliseconds and the modal renders a live progress
      // bar. The backend spawns the commit body as a background task
      // and streams progress via `/status/{job_id}`.
      const { data } = await axios.post(
        `${API}/admin/products/inventory-upload?commit=true&async_job=true`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      const jobId = data.job_id;
      if (!jobId) {
        // Legacy synchronous fallback (server returned no job_id — e.g.
        // when `matched=0` skips the commit block entirely).
        toast({
          title: 'Inventory updated',
          description: `${data.persisted || 0} products updated · ${data.totals.unmatched} unmatched`,
        });
        setInvOpen(false);
        setInvFile(null);
        setInvPreview(null);
        load();
        return;
      }
      const totalProducts = data.totals?.matched
        ? (new Set(data.matched.map((m) => m.product_id))).size
        : 0;
      setInvJobProgress({
        status: 'queued', processed: 0, total: totalProducts, persisted: 0,
      });
      // Poll every 1s until the job resolves. `pollLimit` gives us a
      // 5-minute ceiling so a truly stuck job surfaces via the "still
      // running…" hint instead of spinning forever.
      const pollLimit = 300;
      let attempts = 0;
      const pollOnce = async () => {
        attempts += 1;
        try {
          const { data: st } = await axios.get(
            `${API}/admin/products/inventory-upload/status/${jobId}`,
            { headers: authHeaders },
          );
          setInvJobProgress({
            status: st.status,
            processed: st.processed_products || 0,
            total: st.total_products || totalProducts,
            persisted: st.persisted || 0,
            error: st.error || '',
          });
          if (st.status === 'completed') {
            toast({
              title: 'Inventory updated',
              description: `${st.persisted || 0} products updated · ${data.totals.unmatched} unmatched`,
            });
            setInvOpen(false);
            setInvFile(null);
            setInvPreview(null);
            setInvJobProgress(null);
            setInvApplying(false);
            load();
            return;
          }
          if (st.status === 'failed') {
            toast({ title: 'Apply failed', description: st.error || 'Unknown error' });
            setInvApplying(false);
            return;
          }
          if (attempts >= pollLimit) {
            toast({
              title: 'Still running…',
              description: 'The upload is still processing. The page will refresh when it finishes — you can close this dialog.',
            });
            setInvApplying(false);
            return;
          }
          setTimeout(pollOnce, 1000);
        } catch (err) {
          toast({ title: 'Progress check failed', description: apiErrorMessage(err) });
          setInvApplying(false);
        }
      };
      setTimeout(pollOnce, 500);
    } catch (err) {
      toast({ title: 'Apply failed', description: apiErrorMessage(err) });
      setInvApplying(false);
    }
  };

  // ---- Shareable Stock Report ----
  // Builds a public, signed URL that renders the live Stock Overview as HTML
  // for anyone with the link. The current toolbar filters + columns are
  // baked into the token. Sub-admin category scope is enforced server-side.
  const openShareModal = () => {
    setShareUrl('');
    setShareShortUrl('');
    setShareLinks([]);
    setShareExpiresAt(null);
    setShareCopied(false);
    setShareCopiedSlug('');
    setShareShowLong(false);
    setShareIncludeLogo(true);
    setShareShowValue(canStockValue ? showValue : false);
    setShareCols(
      COLUMNS.reduce((acc, c) => {
        acc[c.key] = !!visibleCols[c.key];
        return acc;
      }, {})
    );
    setShareSelectedCats(
      (categories || []).reduce((acc, c) => {
        acc[c.slug] = (catFilter !== 'all' && catFilter === c.slug);
        return acc;
      }, {})
    );
    setShareMode('single');
    setShareOpen(true);
    // Lazy-load the records list whenever the modal opens so admins
    // immediately see the URL history without an extra click.
    loadShareRecords();
    // Super-admins: fetch current ticker speed so the slider shows the
    // saved value. Sub-admins skip — they don't see the control.
    if (isSuper) loadTickerSettings();
  };

  const generateShareUrl = async () => {
    setShareGenerating(true);
    try {
      const cols = COLUMNS.filter((c) => shareCols[c.key]).map((c) => c.key);
      const selectedSlugs = (categories || [])
        .filter((c) => shareSelectedCats[c.slug])
        .map((c) => c.slug);
      // Feb 2026 — Warehouse scope. Empty list = all warehouses.
      const selectedWarehouseIds = (shareWarehouses || [])
        .filter((w) => shareSelectedWhs[w.id])
        .map((w) => w.id);
      // Defensive UX guard: multi mode needs at least one category picked.
      if (shareMode === 'multi' && selectedSlugs.length === 0) {
        toast({ title: 'Pick at least one division', description: 'Tick the divisions you want to share, or switch back to Single view.' });
        setShareGenerating(false);
        return;
      }
      const basePayload = {
        columns: cols,
        status_filter: statusFilter === 'all' ? null : statusFilter,
        include_logo: shareIncludeLogo,
        show_stock_value: shareShowValue,
        ttl_days: Number(shareTtl),
        label: (shareLabel || '').trim() || null,
        warehouse_ids: selectedWarehouseIds,
      };
      if (shareMode === 'per_category') {
        const { data } = await axios.post(`${API}/admin/stock/share-links`, {
          ...basePayload,
          category_slug: null,
        }, { headers: authHeaders });
        setShareLinks(data.links || []);
        setShareUrl('');
        setShareShortUrl('');
        setShareExpiresAt(data.expires_at || null);
      } else if (shareMode === 'multi') {
        const { data } = await axios.post(`${API}/admin/stock/share-link`, {
          ...basePayload,
          category_slug: null,            // multi wins server-side
          category_slugs: selectedSlugs,
        }, { headers: authHeaders });
        setShareUrl(data.url);
        setShareShortUrl(data.short_url || '');
        setShareLinks([]);
        setShareExpiresAt(data.expires_at);
      } else {
        const { data } = await axios.post(`${API}/admin/stock/share-link`, {
          ...basePayload,
          category_slug: catFilter === 'all' ? null : catFilter,
        }, { headers: authHeaders });
        setShareUrl(data.url);
        setShareShortUrl(data.short_url || '');
        setShareLinks([]);
        setShareExpiresAt(data.expires_at);
      }
      setShareCopied(false);
      setShareCopiedSlug('');
      setShareShowLong(false);
      // Refresh the records panel so the newly-minted URL(s) show up.
      loadShareRecords();
    } catch (err) {
      toast({ title: 'Could not create link', description: apiErrorMessage(err) });
    } finally {
      setShareGenerating(false);
    }
  };

  // Plain clipboard helper — supports modern Clipboard API + textarea
  // fallback for older mobile webviews (in-app Safari, etc).
  const writeClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const copyShareUrl = async () => {
    const target = shareShortUrl || shareUrl;
    if (!target) return;
    try {
      await writeClipboard(target);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch (err) {
      toast({ title: 'Copy failed', description: 'Long-press the link to copy manually.' });
    }
  };

  const copyPerCategoryUrl = async (slug, url) => {
    try {
      await writeClipboard(url);
      setShareCopiedSlug(slug);
      setTimeout(() => setShareCopiedSlug(''), 2200);
    } catch (err) {
      toast({ title: 'Copy failed', description: 'Long-press the link to copy manually.' });
    }
  };

  const copyAllPerCategoryUrls = async () => {
    if (!shareLinks.length) return;
    // Use short URLs when available — they're WhatsApp/SMS friendly.
    const text = shareLinks.map((l) => `${l.category_name}: ${l.short_url || l.url}`).join('\n');
    try {
      await writeClipboard(text);
      setShareCopiedSlug('__all__');
      setTimeout(() => setShareCopiedSlug(''), 2200);
    } catch (err) {
      toast({ title: 'Copy failed', description: 'Try copying each link individually.' });
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-5 md:p-8">
        <div className="flex items-center gap-3 mb-1">
          <Boxes className="w-6 h-6 text-[#0078D4]" />
          <h1 className="text-2xl font-bold tracking-tight text-neutral-900">Stock Overview</h1>
          {isSuper && (
          <a
            href={`${API}/docs/live-stock-guide.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              // Authenticated download: fetch with Bearer header then trigger
              // a blob-URL download. Without this an anchor click would lose
              // the Authorization header and get a 401.
              e.preventDefault();
              (async () => {
                try {
                  const resp = await axios.get(`${API}/docs/live-stock-guide.pdf`, {
                    headers: authHeaders, responseType: 'blob',
                  });
                  const blob = new Blob([resp.data], { type: 'application/pdf' });
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank', 'noopener,noreferrer');
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (err) {
                  toast({ title: 'Could not open guide', description: apiErrorMessage(err) });
                }
              })();
            }}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#0078D4]/30 text-[#0078D4] bg-[#0078D4]/5 hover:bg-[#0078D4]/10 transition"
            title="Open the Live Stock Report Feature Guide (PDF)"
            data-testid="open-live-stock-guide"
          >
            <FileText className="w-3.5 h-3.5" />
            Read Guide
          </a>
          )}
          {isSuper && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm('⚠️ EMERGENCY ACTION\n\nThis will INVALIDATE every device cookie currently in use to access stock reports. Every approved viewer will land on the request form on their next visit and must re-identify themselves.\n\nUse this only when a link/cookie may have leaked.\n\nProceed?')) return;
                try {
                  const { data } = await axios.post(
                    `${API}/admin/stock/access/invalidate-all-cookies`,
                    null,
                    { headers: authHeaders },
                  );
                  toast({
                    title: 'All access cookies invalidated',
                    description: `Cookie epoch is now ${data.cookie_epoch}. Every viewer must re-identify on their next visit.`,
                  });
                } catch (err) {
                  toast({ title: 'Failed', description: apiErrorMessage(err) });
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 transition"
              title="Emergency: invalidate every device cookie (forces all approved viewers to re-identify on next visit)"
              data-testid="invalidate-all-stock-cookies"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Burn cookies
            </button>
          )}
        </div>
        <p className="text-sm text-neutral-500 mb-5">
          {kpis.total} products · {kpis.out_of_stock} out of stock · {kpis.coming_soon} coming soon
        </p>

        {/* Super-admin only: top-level page tabs. Sub-admins skip the tab
            strip entirely so the UI stays simple for them. */}
        {isSuper && (
          <div className="flex items-center gap-1 mb-5 bg-white border border-neutral-200 rounded-xl p-1 w-fit">
            <button
              type="button"
              onClick={() => setActiveTab('stock')}
              data-testid="tab-stock"
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${activeTab === 'stock' ? 'bg-[#0078D4] text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
            >
              <Boxes className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Stock Overview
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('access')}
              data-testid="tab-access"
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition relative ${activeTab === 'access' ? 'bg-[#0078D4] text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
            >
              <ShieldCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Access Control
              {accessPending > 0 && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full ${activeTab === 'access' ? 'bg-white text-[#0078D4]' : 'bg-rose-500 text-white animate-pulse'}`}
                  data-testid="tab-access-badge"
                >
                  {accessPending}
                </span>
              )}
            </button>
          </div>
        )}

        {/* In-app banner: a new IP is awaiting approval. Visible everywhere
            on the Stock page for the super-admin until they switch to the
            Access Control tab. */}
        {isSuper && accessPending > 0 && activeTab !== 'access' && (
          <div
            className="mb-4 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3"
            data-testid="access-pending-banner"
          >
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="w-5 h-5 text-amber-700" />
              <div className="text-sm">
                <strong>{accessPending}</strong> {accessPending === 1 ? 'visitor is' : 'visitors are'} waiting for live-stock access.
              </div>
            </div>
            <Button size="sm" onClick={() => setActiveTab('access')} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="access-pending-banner-cta">
              Review now
            </Button>
          </div>
        )}

        {activeTab === 'access' && isSuper ? (
          <StockAccessControl authHeaders={authHeaders} />
        ) : (
        <>
        {/* KPI cards */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <KpiCard label="In stock"    value={kpis.in_stock}    accent="border-emerald-200" testId="kpi-in" />
          <KpiCard label="Out of stock" value={kpis.out_of_stock} accent="border-rose-200"   testId="kpi-out" />
          <KpiCard label="Coming soon" value={kpis.coming_soon} accent="border-amber-200"   testId="kpi-soon" />
          {canStockValue && (showValue ? (
            <div className="flex-1 rounded-2xl border border-sky-200 p-4 bg-white relative" data-testid="kpi-value">
              <button
                type="button"
                onClick={() => setShowValue(false)}
                className="absolute top-2 right-2 p-1 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100"
                title="Hide stock value"
                data-testid="kpi-value-hide"
              >
                <EyeOff className="w-4 h-4" />
              </button>
              <div className="text-2xl font-extrabold text-neutral-900"><Rial /> {kpis.value.toFixed(0)}</div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mt-0.5">Stock value</div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowValue(true)}
              className="flex-1 sm:max-w-[180px] rounded-2xl border border-dashed border-neutral-300 p-4 bg-white text-neutral-500 hover:text-[#0078D4] hover:border-[#0078D4] flex items-center justify-center gap-2 text-sm font-semibold transition"
              data-testid="kpi-value-show"
              title="Show stock value"
            >
              <Eye className="w-4 h-4" /> Show stock value
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-neutral-200 rounded-xl p-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, pack, SKU, pallet # or location…"
              className="pl-9"
              data-testid="stock-search"
            />
          </div>
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            className="h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm"
            data-testid="stock-cat-filter"
          >
            <option value="all">All divisions</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          {/* Feb 2026 — top-of-page warehouse filter (renders only
              for multi-site users). */}
          <WarehouseFilterSelect
            storageKey="stock"
            warehouses={whList}
            value={whFilter}
            onChange={setWhFilter}
            className="h-10 px-3 rounded-md border border-neutral-300 bg-white"
          />
          {/* Oct 2026 — Searchable Location filter. Typing filters the
              popover list; clicking a row commits the selection; the
              × badge on the trigger clears it. Hidden when there are
              no locations in the current scope so an empty database
              doesn't surface a dead control. */}
          {allLocations.length > 0 && (
            <div ref={locBoxRef} className="relative">
              <button
                type="button"
                onClick={() => setLocOpen((o) => !o)}
                className={`h-10 px-3 rounded-md border bg-white text-sm flex items-center gap-2 min-w-[150px] text-left ${locFilter ? 'border-[#0078D4] text-[#0078D4]' : 'border-neutral-300 text-neutral-700'}`}
                data-testid="stock-location-filter-btn"
              >
                <MapPin className="w-4 h-4" />
                <span className="flex-1 truncate">{locFilter || 'All locations'}</span>
                {locFilter ? (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Clear location filter"
                    onClick={(e) => { e.stopPropagation(); setLocFilter(''); setLocQuery(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setLocFilter(''); setLocQuery(''); } }}
                    className="text-neutral-400 hover:text-red-600 cursor-pointer"
                    data-testid="stock-location-filter-clear"
                  >
                    <X className="w-4 h-4" />
                  </span>
                ) : (
                  <ChevronDown className="w-4 h-4 text-neutral-400" />
                )}
              </button>
              {locOpen && (
                <div
                  className="absolute left-0 top-11 z-30 w-72 bg-white rounded-lg border border-neutral-200 shadow-xl p-2"
                  data-testid="stock-location-filter-popover"
                >
                  <input
                    autoFocus
                    type="text"
                    value={locQuery}
                    onChange={(e) => setLocQuery(e.target.value)}
                    placeholder={`Search ${allLocations.length} location${allLocations.length === 1 ? '' : 's'}…`}
                    className="w-full h-9 px-3 rounded-md border border-neutral-300 text-sm mb-2 focus:border-[#0078D4] focus:outline-none focus:ring-1 focus:ring-[#0078D4]"
                    data-testid="stock-location-filter-search"
                  />
                  <div className="max-h-72 overflow-y-auto">
                    {locationMatches.length === 0 ? (
                      <div className="px-2 py-6 text-center text-xs text-neutral-500">
                        No locations match.
                      </div>
                    ) : (
                      locationMatches.map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => { setLocFilter(l); setLocOpen(false); setLocQuery(''); }}
                          className={`w-full text-left px-2 py-1.5 rounded hover:bg-neutral-50 text-sm font-mono tabular-nums ${locFilter === l ? 'bg-[#E6F2FB] text-[#0078D4] font-semibold' : 'text-neutral-700'}`}
                          data-testid={`stock-location-filter-opt-${l}`}
                        >
                          {l}
                        </button>
                      ))
                    )}
                    {locationMatches.length === 100 && (
                      <div className="px-2 py-1.5 text-[10px] text-neutral-400 italic">
                        Showing first 100 — refine search.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm"
            data-testid="stock-status-filter"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="relative">
            <Button variant="outline" onClick={() => setColsOpen((o) => !o)} data-testid="stock-cols-btn">
              <Cog className="w-4 h-4 mr-1" /> Columns
            </Button>
            {colsOpen && (
              <div className="absolute right-0 top-11 z-20 w-56 bg-white rounded-lg border border-neutral-200 shadow-xl p-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 px-2 py-1">Show columns</div>
                {COLUMNS.filter((c) => {
                  if (c.key === 'sku' && !canSku) return false;
                  if (c.key === 'qty' && !canQty) return false;
                  if (c.key === 'price' && !canPrice) return false;
                  return true;
                }).map((c) => (
                  <label key={c.key} className={`flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-50 text-sm ${c.required ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={!!visibleCols[c.key]}
                      disabled={c.required}
                      onChange={(e) => setVisibleCols({ ...visibleCols, [c.key]: e.target.checked })}
                      data-testid={`stock-col-${c.key}`}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          {canStockDownload && (
            <Button variant="outline" onClick={openExportModal} data-testid="stock-export-xlsx">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Download Stock
            </Button>
          )}
          {canStockUpload && (
            <Button variant="outline" onClick={() => setInvOpen(true)} data-testid="stock-inventory-btn">
              <Upload className="w-4 h-4 mr-1" /> Update Stock
            </Button>
          )}
          {canStockReport && (
            <Button variant="outline" onClick={openShareModal} data-testid="stock-share-btn">
              <Link2 className="w-4 h-4 mr-1" /> Share Link
            </Button>
          )}
          {canStockReport && (
            <Button onClick={openEmailModal} data-testid="stock-email-btn" className="bg-[#0078D4] hover:bg-[#0066B8] text-white">
              <Mail className="w-4 h-4 mr-1" /> Send Email
            </Button>
          )}
        </div>

        {/* Groups */}
        {loading ? (
          <div className="text-center text-neutral-400 py-12">Loading…</div>
        ) : !groups.length ? (
          <div className="text-center text-neutral-400 py-12">No products match the current filters.</div>
        ) : groups.map((g) => {
          const isCollapsed = !!collapsed[g.slug];
          const isEmpty = !g.items.length;
          const counts = { in: 0, out: 0, soon: 0 };
          g.items.forEach((p) => {
            const s = statusOf(p);
            if (s === 'in_stock') counts.in++;
            else if (s === 'out_of_stock') counts.out++;
            else counts.soon++;
          });
          return (
            <div key={g.slug} className="mb-4 bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid={`stock-group-${g.slug}`}>
              <button
                type="button"
                onClick={() => setCollapsed({ ...collapsed, [g.slug]: !isCollapsed })}
                className="w-full flex items-center gap-2 px-4 py-3 hover:bg-neutral-50 border-b border-neutral-100"
                data-testid={`stock-group-toggle-${g.slug}`}
              >
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <span className="font-bold text-neutral-800">{g.name}</span>
                {isEmpty ? (
                  <span className="text-xs text-neutral-500 italic">No products yet</span>
                ) : (
                  <span className="text-xs text-neutral-500">
                    {g.items.length} items · <span className="text-emerald-600">{counts.in} in</span>
                    {counts.out > 0 && <> · <span className="text-rose-600">{counts.out} out</span></>}
                    {counts.soon > 0 && <> · <span className="text-amber-600">{counts.soon} coming</span></>}
                  </span>
                )}
              </button>
              {!isCollapsed && (
                isEmpty ? (
                  <div className="px-4 py-6 text-center text-xs text-neutral-500 bg-neutral-50/40" data-testid={`stock-group-empty-${g.slug}`}>
                    No products in this division yet. Use <span className="font-semibold text-neutral-700">Share Link</span> or the email recipient settings above to wire it up — it&apos;s skipped from auto-trigger emails until products are added.
                  </div>
                ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                    <tr>
                      {visibleCols.image  && <th className="px-3 py-2 text-left w-14">Image</th>}
                      {canSku && visibleCols.sku    && <th className="px-3 py-2 text-left">SKU</th>}
                      {visibleCols.name   && <th className="px-3 py-2 text-left">Name</th>}
                      {visibleCols.pack   && <th className="px-3 py-2 text-left">Pack</th>}
                      {canQty && visibleCols.qty    && <th className="px-3 py-2 text-center">On hand</th>}
                      {canPrice && visibleCols.price  && <th className="px-3 py-2 text-right">Price</th>}
                      {visibleCols.vat    && <th className="px-3 py-2 text-center">VAT</th>}
                      {visibleCols.uom    && <th className="px-3 py-2 text-center">UOM</th>}
                      {visibleCols.tag    && <th className="px-3 py-2 text-left">Tag</th>}
                      {visibleCols.minqty && <th className="px-3 py-2 text-center">Min</th>}
                      {visibleCols.status && <th className="px-3 py-2 text-center">Status</th>}
                      {visibleCols.hold      && <th className="px-3 py-2 text-center">Hold</th>}
                      {visibleCols.allocated && <th className="px-3 py-2 text-center">Allocated</th>}
                      {visibleCols.pallet    && <th className="px-3 py-2 text-left">PL #</th>}
                      {visibleCols.location  && <th className="px-3 py-2 text-left">Loc</th>}
                      {visibleCols.zone      && <th className="px-3 py-2 text-left">Zone</th>}
                      {visibleCols.store     && (
                        <th className="px-3 py-2 text-left select-none">
                          {/* Feb 2026 — Store header. Click cycles the
                              storeSort state (none → asc → desc → none)
                              and lets ops group by warehouse without
                              leaving the Stock Report. */}
                          <button
                            type="button"
                            onClick={() => setStoreSort(
                              storeSort === 'none' ? 'asc'
                                : storeSort === 'asc' ? 'desc'
                                : 'none'
                            )}
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-[#0078D4] transition-colors"
                            data-testid="stock-store-header"
                            title="Sort by warehouse (asc / desc / off)"
                          >
                            <span>Store</span>
                            <span className="text-[9px] text-neutral-400" aria-hidden="true">
                              {storeSort === 'asc' ? '↑' : storeSort === 'desc' ? '↓' : '⇅'}
                            </span>
                          </button>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((p) => (
                      <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50/50" data-testid={`stock-row-${p.id}`}>
                        {visibleCols.image && (
                          <td className="px-3 py-2">
                            {p.image
                              ? <img src={p.image} alt="" className="w-10 h-10 object-contain rounded bg-neutral-50" loading="lazy" />
                              : <div className="w-10 h-10 rounded bg-neutral-100" />
                            }
                          </td>
                        )}
                        {canSku && visibleCols.sku    && (
                          <td className="px-3 py-2 font-mono text-xs text-neutral-600">
                            <span className="inline-flex items-center gap-1.5">
                              {p.sku || '—'}
                              {/* Feb 2026 (user directive) — Ledger Drift chip.
                                * Renders next to the SKU when the ledger total
                                * (product.stock_qty) diverges from the physical
                                * rack total (Σ warehouse_placements.qty). Tooltip
                                * carries the signed delta so ops eyes see the
                                * exact discrepancy without leaving the row. */}
                              {Number.isFinite(p.ledger_drift) && p.ledger_drift !== 0 && (
                                <span
                                  className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200"
                                  title={`Ledger Drift: ${p.ledger_drift > 0 ? '+' : ''}${p.ledger_drift} units (ledger ${p.stock_qty ?? 0} vs rack ${p.placements_sum_qty ?? 0})`}
                                  data-testid={`ledger-drift-chip-${p.id}`}
                                >
                                  <AlertTriangle className="w-2.5 h-2.5" />
                                  {p.ledger_drift > 0 ? '+' : ''}{p.ledger_drift}
                                </span>
                              )}
                            </span>
                          </td>
                        )}
                        {visibleCols.name   && (
                          <td className="px-3 py-2 font-medium text-neutral-800">
                            <span className="inline-flex items-center gap-1.5">
                              {p.name}
                              {ledgerCanView && (
                                <button
                                  onClick={() => setLedgerFor(p)}
                                  className="p-1 rounded hover:bg-[#0078D4]/10 text-neutral-400 hover:text-[#0078D4]"
                                  title="Edit in/out ledger"
                                  data-testid={`ledger-open-${p.id}`}
                                >
                                  <History className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </span>
                          </td>
                        )}
                        {visibleCols.pack   && <td className="px-3 py-2 text-neutral-600">{p.size || '—'}</td>}
                        {canQty && visibleCols.qty    && <td className="px-3 py-2 text-center tabular-nums font-semibold text-neutral-800">{onHandOf(p)}</td>}
                        {canPrice && visibleCols.price  && <td className="px-3 py-2 text-right tabular-nums"><Rial /> {Number(p.price || 0).toFixed(3)}</td>}
                        {visibleCols.vat    && <td className="px-3 py-2 text-center text-xs">{p.vat_applicable ? '5%' : '—'}</td>}
                        {visibleCols.uom    && <td className="px-3 py-2 text-center text-xs uppercase">{p.uom || 'ctn'}</td>}
                        {visibleCols.tag    && <td className="px-3 py-2 text-xs">{p.tag || ''}</td>}
                        {visibleCols.minqty && <td className="px-3 py-2 text-center">{p.min_qty || 1}</td>}
                        {visibleCols.status && (
                          <td className="px-3 py-2 text-center">
                            <StatusPill value={statusOf(p)} onChange={(v) => setStatus(p, v)} disabled={!!updating[p.id]} />
                          </td>
                        )}
                        {/* Feb 2026 warehouse-side columns. Compact
                            renderer: multi-pallet / multi-location
                            products show "FIRST +N" so the row height
                            stays predictable. */}
                        {visibleCols.hold && (
                          <td className="px-3 py-2 text-center tabular-nums" data-testid={`stock-hold-${p.id}`}>
                            {(() => {
                              const h = Number((warehouseByProduct[p.id] || {}).hold_qty || 0);
                              return h > 0
                                ? <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">{h}</span>
                                : <span className="text-neutral-400">—</span>;
                            })()}
                          </td>
                        )}
                        {visibleCols.allocated && (
                          <td className="px-3 py-2 text-center tabular-nums text-neutral-700" data-testid={`stock-allocated-${p.id}`}>
                            {Number((warehouseByProduct[p.id] || {}).allocated_qty || 0)}
                          </td>
                        )}
                        {visibleCols.pallet && (
                          <td className="px-3 py-2 font-mono text-xs text-neutral-700" data-testid={`stock-pallet-${p.id}`}>
                            {(() => {
                              const bucket = warehouseByProduct[p.id] || {};
                              const list = Array.isArray(bucket.pallets) ? bucket.pallets : [];
                              if (list.length === 0) return '—';
                              if (list.length <= 2) return list.join(', ');
                              return `${list[0]} +${list.length - 1}`;
                            })()}
                          </td>
                        )}
                        {visibleCols.location && (
                          <td className="px-3 py-2 font-mono text-xs text-neutral-700" data-testid={`stock-location-${p.id}`}>
                            {(() => {
                              const bucket = warehouseByProduct[p.id] || {};
                              const list = Array.isArray(bucket.locations) ? bucket.locations : [];
                              if (list.length === 0) return '—';
                              if (list.length <= 2) return list.join(', ');
                              return `${list[0]} +${list.length - 1}`;
                            })()}
                          </td>
                        )}
                        {visibleCols.zone && (
                          <td className="px-3 py-2 text-xs" data-testid={`stock-zone-${p.id}`}>
                            {(() => {
                              // Feb 2026 — derive distinct zones from
                              // location list. Location convention is
                              // `ZONE-BAY-SLOT` (e.g. `GROUND-A-01`);
                              // some legacy rows have no dash, in
                              // which case the whole label is the
                              // zone. Rendered as coloured chips so
                              // multi-zone products stand out.
                              const bucket = warehouseByProduct[p.id] || {};
                              const list = Array.isArray(bucket.locations) ? bucket.locations : [];
                              const zones = Array.from(new Set(
                                list.map((l) => {
                                  const s = String(l || '').trim();
                                  if (!s) return '';
                                  return s.includes('-') ? s.split('-')[0] : s;
                                }).filter(Boolean),
                              ));
                              if (zones.length === 0) return <span className="text-neutral-400">—</span>;
                              return (
                                <div className="flex flex-wrap gap-1">
                                  {zones.map((z) => (
                                    <span
                                      key={z}
                                      className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 px-2 py-0.5 text-[11px] font-semibold font-mono"
                                      data-testid={`stock-zone-chip-${p.id}-${z}`}
                                    >
                                      {z}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                        {visibleCols.store && (
                          <td className="px-3 py-2 text-xs text-neutral-700" data-testid={`stock-store-${p.id}`}>
                            {(() => {
                              const bucket = warehouseByProduct[p.id] || {};
                              const list = Array.isArray(bucket.warehouses) ? bucket.warehouses : [];
                              if (list.length === 0) {
                                return <span className="text-neutral-400">—</span>;
                              }
                              if (list.length === 1) {
                                return (
                                  <span
                                    className="inline-flex items-center rounded-full bg-sky-50 border border-sky-200 text-sky-800 px-2 py-0.5 text-[11px] font-semibold"
                                    title={list[0]}
                                  >
                                    {list[0]}
                                  </span>
                                );
                              }
                              return (
                                <span
                                  className="inline-flex items-center rounded-full bg-sky-50 border border-sky-200 text-sky-800 px-2 py-0.5 text-[11px] font-semibold"
                                  title={list.join(', ')}
                                >
                                  {list[0]} +{list.length - 1}
                                </span>
                              );
                            })()}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                )
              )}
            </div>
          );
        })}
        </>
        )}
      </div>

      {/* Manual ledger editor (super admin) */}
      {ledgerFor && (
        <LedgerEditor product={ledgerFor} onClose={() => setLedgerFor(null)} API={API} authHeaders={authHeaders} canEdit={ledgerCanEdit} />
      )}

      {/* Stock Export Column Selection Modal (Feb 2026) */}
      {exportOpen && (
        <div
          className="fixed inset-0 z-50 bg-neutral-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => { if (!exportBusy) setExportOpen(false); }}
          data-testid="stock-export-modal"
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#0078D4]" />
                <h2 className="text-lg font-bold text-neutral-900">Pick columns to export</h2>
              </div>
              <button
                type="button"
                onClick={() => { if (!exportBusy) setExportOpen(false); }}
                className="text-neutral-400 hover:text-neutral-700 text-xl leading-none px-2"
                data-testid="stock-export-modal-close"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-neutral-600">
                On-sheet order is fixed so the file still round-trips through the uploader. Only the ticked columns are written.
              </p>
              <p className="text-xs text-neutral-500" data-testid="stock-export-scope">
                Exporting <b className="text-neutral-800">{(filtered || []).length}</b> product{(filtered || []).length === 1 ? '' : 's'} matching your current filters.
              </p>
              {/* Feb 2026 — aggregate-vs-per-pallet toggle. On = one
                  row per product (Qty/Allocated/Hold summed, Loc/Plt/
                  Batch/dates blank). Off = per-pallet fan-out. */}
              <label
                className="flex items-start gap-3 px-3 py-2 rounded-lg border border-neutral-200 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
                data-testid="stock-export-one-row-per-item"
              >
                <input
                  type="checkbox"
                  checked={exportOneRowPerItem}
                  onChange={(e) => setExportOneRowPerItem(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#0078D4]"
                  data-testid="stock-export-one-row-per-item-checkbox"
                />
                <span className="text-sm text-neutral-800">
                  <span className="font-medium">One row per item</span>
                  <span className="block text-xs text-neutral-500 mt-0.5">
                    Sum Qty / Allocated / Hold across placements. Loc, Plt, Batch and date columns render blank.
                  </span>
                </span>
              </label>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setExportCols(Object.fromEntries(STOCK_EXPORT_COLUMNS.map((c) => [c.key, true])))}
                  className="px-2 py-1 rounded border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                  data-testid="stock-export-select-all"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setExportCols(Object.fromEntries(STOCK_EXPORT_COLUMNS.map((c) => [c.key, false])))}
                  className="px-2 py-1 rounded border border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                  data-testid="stock-export-clear-all"
                >
                  Clear all
                </button>
              </div>
              <div className="rounded-lg border border-neutral-200 divide-y divide-neutral-100">
                {STOCK_EXPORT_COLUMNS.map((c) => (
                  <label
                    key={c.key}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-neutral-50"
                    data-testid={`stock-export-col-${c.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!exportCols[c.key]}
                      onChange={(e) => setExportCols({ ...exportCols, [c.key]: e.target.checked })}
                      className="w-4 h-4 accent-[#0078D4]"
                    />
                    <span className="text-sm text-neutral-800">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-neutral-200 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setExportOpen(false)}
                disabled={exportBusy}
                data-testid="stock-export-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={downloadInvExport}
                disabled={exportBusy}
                className="bg-[#0078D4] hover:bg-[#0066B8] text-white"
                data-testid="stock-export-download"
              >
                {exportBusy ? 'Downloading…' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Email Report Modal */}
      {/* Inventory Bulk Upload Modal */}
      {invOpen && (
        <div
          className="fixed inset-0 z-50 bg-neutral-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => { if (!invUploading && !invApplying) { setInvOpen(false); setInvFile(null); setInvPreview(null); } }}
          data-testid="stock-inventory-modal"
        >
          <div
            className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#0078D4]" />
                <h2 className="text-lg font-bold text-neutral-900">Bulk Stock Update</h2>
              </div>
              <button
                onClick={() => { setInvOpen(false); setInvFile(null); setInvPreview(null); }}
                className="p-1.5 rounded-md hover:bg-neutral-100 text-neutral-500"
                data-testid="stock-inventory-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Step 1: template + file picker */}
              {!invPreview && (
                <>
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 space-y-1.5">
                    <div className="font-bold text-neutral-700 uppercase tracking-wider text-[10px]">How it works</div>
                    <div>1. Download the template below and fill three columns: <strong>Item Code</strong>, <strong>Item Name</strong>, <strong>Qty</strong>.</div>
                    <div>2. Upload it here — we&apos;ll match by <strong>Item Code → product SKU</strong> and show a preview.</div>
                    <div>3. Review matches, then click <strong>Apply</strong> to overwrite on-hand quantities.</div>
                    <div className="pt-1.5 border-t border-neutral-200 mt-1.5 text-neutral-500">
                      Status auto-updates: <strong className="text-emerald-700">qty &gt; 0 → In stock</strong>, <strong className="text-rose-700">qty = 0 → Out of stock</strong>.
                    </div>
                  </div>
                  <Button variant="outline" onClick={downloadInvTemplate} data-testid="stock-inventory-template-btn" className="w-full">
                    <Download className="w-4 h-4 mr-1" /> Download xlsx template
                  </Button>
                  <label
                    className={`block w-full rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${invUploading ? 'border-neutral-200 bg-neutral-50 opacity-60' : 'border-neutral-300 hover:border-[#0078D4] hover:bg-[#0078D4]/5'}`}
                    data-testid="stock-inventory-dropzone"
                  >
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                      className="hidden"
                      disabled={invUploading}
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) { setInvFile(f); previewInventory(f); }
                      }}
                      data-testid="stock-inventory-file"
                    />
                    <Upload className="w-7 h-7 text-neutral-400 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-neutral-800">
                      {invUploading ? 'Reading file…' : (invFile ? invFile.name : 'Click to choose .xlsx or .csv')}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-1">Max 5 MB · headers: Item Code, Item Name, Qty</div>
                  </label>
                </>
              )}

              {/* Step 2: preview */}
              {invPreview && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                      <div className="text-2xl font-extrabold text-emerald-700">{invPreview.totals.matched}</div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mt-0.5">Matched</div>
                    </div>
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                      <div className="text-2xl font-extrabold text-amber-700">{invPreview.totals.unmatched}</div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-0.5">Unmatched</div>
                    </div>
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-center">
                      <div className="text-2xl font-extrabold text-rose-700">{invPreview.totals.invalid}</div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 mt-0.5">Invalid</div>
                    </div>
                  </div>

                  {invPreview.matched.length > 0 && (
                    <div className="rounded-lg border border-emerald-200 overflow-hidden">
                      <div className="px-3 py-2 bg-emerald-50 text-xs font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Matched ({invPreview.matched.length})
                      </div>
                      <div className="max-h-56 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-emerald-50/60 text-[10px] uppercase tracking-wider text-emerald-700">
                            <tr>
                              <th className="px-3 py-1.5 text-left">SKU</th>
                              <th className="px-3 py-1.5 text-left">Product</th>
                              <th className="px-3 py-1.5 text-right">Old</th>
                              <th className="px-3 py-1.5 text-right">New</th>
                              <th className="px-3 py-1.5 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-emerald-100">
                            {invPreview.matched.map((m, i) => (
                              <tr key={i} data-testid={`inv-match-${i}`}>
                                <td className="px-3 py-1.5 font-mono text-neutral-600">{m.item_code}</td>
                                <td className="px-3 py-1.5 text-neutral-800">{m.product_name}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-neutral-500">{m.old_qty}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums font-bold text-emerald-700">{m.new_qty}</td>
                                <td className="px-3 py-1.5 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${m.new_status === 'in_stock' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {m.new_status === 'in_stock' ? 'In stock' : 'Out of stock'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {invPreview.unmatched.length > 0 && (
                    <div className="rounded-lg border border-amber-200 overflow-hidden">
                      <div className="px-3 py-2 bg-amber-50 text-xs font-bold uppercase tracking-wider text-amber-800 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" /> Unmatched — no SKU in catalog ({invPreview.unmatched.length})
                      </div>
                      <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <tbody className="divide-y divide-amber-100">
                            {invPreview.unmatched.map((u, i) => (
                              <tr key={i}>
                                <td className="px-3 py-1.5 font-mono text-neutral-600 w-32">{u.item_code}</td>
                                <td className="px-3 py-1.5 text-neutral-700">{u.sheet_name || '—'}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-neutral-500">qty: {u.qty}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {invPreview.invalid.length > 0 && (
                    <div className="rounded-lg border border-rose-200 overflow-hidden">
                      <div className="px-3 py-2 bg-rose-50 text-xs font-bold uppercase tracking-wider text-rose-800">Invalid rows</div>
                      <div className="max-h-32 overflow-y-auto px-3 py-2 space-y-1">
                        {invPreview.invalid.map((v, i) => (
                          <div key={i} className="text-xs text-rose-700">Row {v.row}: {v.reason}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex flex-col gap-2 px-5 py-4 border-t border-neutral-200 sticky bottom-0 bg-white">
              {/* Feb 2026 — Strict placement: mandatory warehouse
                  destination for the Apply step. Rendered above the
                  progress bar so the picker is visible before + after
                  the admin hits Apply. Preview step (no invPreview
                  yet) skips it to keep the drop-zone clean. */}
              {invPreview && (
                <div
                  className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2"
                  data-testid="stock-inventory-warehouse-picker"
                >
                  <label className="text-[10px] uppercase tracking-wider font-bold text-amber-800 block">
                    Destination warehouse <span className="text-rose-600">*</span>
                  </label>
                  <select
                    value={invWarehouseId}
                    onChange={(e) => setInvWarehouseId(e.target.value)}
                    disabled={invApplying}
                    className="mt-1 w-full rounded-md border border-amber-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    data-testid="stock-inventory-warehouse-select"
                  >
                    <option value="">Select a warehouse…</option>
                    {/* Feb 2026 — Warehouse dropdown is filtered to the
                        user's own assignment via `/admin/me` payload.
                        Super admin + admin-role users (`warehouseScopeAll`)
                        see every warehouse; everyone else sees only the
                        ids in their `warehouse_scope`. `whList` is the
                        full-catalog source; we intersect against
                        `myWarehouses.id` for the picker. */}
                    {(() => {
                      const full = whList || [];
                      const allowed = warehouseScopeAll
                        ? full
                        : full.filter((w) => (myWarehouses || []).some((mw) => mw.id === w.id));
                      return allowed.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}{w.is_default ? ' (Default)' : ''}
                        </option>
                      ));
                    })()}
                  </select>
                  <div className="mt-1 text-[10px] text-amber-800/80 leading-snug">
                    All uploaded stock will land in this warehouse only — regardless of item category.
                  </div>
                </div>
              )}
              {invJobProgress && (
                <div
                  className="rounded-lg border border-[#0078D4]/30 bg-[#0078D4]/5 px-3 py-2"
                  data-testid="stock-inventory-progress"
                >
                  <div className="flex items-center justify-between text-xs font-semibold text-[#0078D4]">
                    <span>
                      {invJobProgress.status === 'queued' && 'Queued…'}
                      {invJobProgress.status === 'running' && 'Applying updates…'}
                      {invJobProgress.status === 'completed' && 'Completed'}
                      {invJobProgress.status === 'failed' && 'Failed'}
                    </span>
                    <span
                      className="tabular-nums"
                      data-testid="stock-inventory-progress-count"
                    >
                      {invJobProgress.processed} of {invJobProgress.total} products
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full rounded-full bg-white overflow-hidden">
                    <div
                      className={`h-full ${invJobProgress.status === 'failed' ? 'bg-red-500' : 'bg-[#0078D4]'} transition-all duration-300`}
                      style={{
                        width: `${invJobProgress.total > 0
                          ? Math.min(100, Math.round((invJobProgress.processed / invJobProgress.total) * 100))
                          : (invJobProgress.status === 'queued' ? 5 : 100)}%`,
                      }}
                    />
                  </div>
                  {invJobProgress.error && (
                    <div className="mt-1 text-[11px] text-red-700">{invJobProgress.error}</div>
                  )}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                {invPreview ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { setInvPreview(null); setInvFile(null); }}
                    className="flex-1"
                    disabled={invApplying}
                    data-testid="stock-inventory-back"
                  >
                    Choose another file
                  </Button>
                  <Button
                    onClick={applyInventory}
                    disabled={invApplying || invPreview.totals.matched === 0 || !invWarehouseId}
                    className="flex-1 bg-[#0078D4] hover:bg-[#0066B8] text-white"
                    data-testid="stock-inventory-apply"
                    title={!invWarehouseId ? 'Pick a destination warehouse first' : undefined}
                  >
                    {invApplying ? 'Applying…' : `Apply ${invPreview.totals.matched} update${invPreview.totals.matched === 1 ? '' : 's'}`}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => { setInvOpen(false); setInvFile(null); setInvPreview(null); }}
                  className="w-full"
                  data-testid="stock-inventory-cancel"
                  disabled={invUploading}
                >
                  Cancel
                </Button>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {emailOpen && (
        <div
          className="fixed inset-0 z-50 bg-neutral-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setEmailOpen(false)}
          data-testid="stock-email-modal"
        >
          <div
            className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#0078D4]" />
                <h2 className="text-lg font-bold text-neutral-900">Send Stock Report</h2>
              </div>
              <button
                onClick={() => setEmailOpen(false)}
                className="p-1.5 rounded-md hover:bg-neutral-100 text-neutral-500"
                data-testid="stock-email-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Recipients <span className="text-neutral-400 font-normal lowercase tracking-normal">— gets the full report</span>
                </label>
                <textarea
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  placeholder="boss@example.com, warehouse@example.com…"
                  rows={2}
                  className="w-full px-3 py-2 rounded-md border border-neutral-300 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30 focus:border-[#0078D4]"
                  data-testid="stock-email-recipients"
                />
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={alsoSendGlobal}
                    onChange={(e) => setAlsoSendGlobal(e.target.checked)}
                    className="w-4 h-4 accent-[#0078D4]"
                    data-testid="stock-email-also-global"
                  />
                  <span className="text-xs text-neutral-700">Send the full report to all recipients above</span>
                </label>
                <p className="text-[11px] text-neutral-400 mt-1">Comma, semicolon or newline separated.</p>
                {/* Save-only affordance — the global recipients list is
                    persisted as part of the bigger schedule document, so
                    without an explicit button right here the admin had
                    to scroll down to "Save weekly schedule" and assume
                    that buried button also saved the list (it does, but
                    the label hid the fact). This dedicated button makes
                    the save action unambiguous. */}
                <div className="mt-2 flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => saveRecipientsOnly('global')}
                    disabled={savingRecipients || savingSched}
                    className="h-8 px-3 text-xs rounded-full border-neutral-300 hover:border-[#0078D4] hover:text-[#0078D4]"
                    data-testid="stock-email-save-recipients"
                  >
                    {savingRecipients ? 'Saving…' : 'Save recipients'}
                  </Button>
                </div>
              </div>

              {/* Per-category recipients */}
              <div className="rounded-lg border border-neutral-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCatRecipExpanded((x) => !x)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-neutral-50"
                  data-testid="stock-cat-recipients-toggle"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-bold text-neutral-800">Per-division recipients</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5">
                      {Object.values(catRecipients).filter((v) => (v || '').trim()).length} configured
                    </span>
                  </span>
                  {catRecipExpanded ? <ChevronDown className="w-4 h-4 text-neutral-500" /> : <ChevronRight className="w-4 h-4 text-neutral-500" />}
                </button>
                {catRecipExpanded && (
                  <div className="px-3 pb-3 pt-1 space-y-2 border-t border-neutral-200 bg-neutral-50/50">
                    <p className="text-[11px] text-neutral-500 pt-2">
                      Each category with recipients here gets its OWN filtered email (only that category&apos;s products), sent to its list. Applies to BOTH the Stock Overview digest and the card-view auto-email after every bulk upload.
                    </p>
                    {categories.length === 0 ? (
                      <p className="text-xs text-neutral-400 italic py-2">No divisions yet.</p>
                    ) : categories.map((c) => (
                      <div key={c.slug} className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <label className="sm:w-32 shrink-0 text-xs font-semibold text-neutral-700 truncate" title={c.name}>
                          {c.name}
                        </label>
                        <Input
                          value={catRecipients[c.slug] || ''}
                          onChange={(e) => setCatRecipients({ ...catRecipients, [c.slug]: e.target.value })}
                          placeholder="email1@x.com, email2@x.com"
                          className="flex-1 h-9 text-sm"
                          data-testid={`stock-cat-recip-${c.slug}`}
                        />
                      </div>
                    ))}
                    {/* Dedicated save action for the per-category lists.
                        These were silently lost before because the only
                        save path was buried under "Save weekly schedule"
                        at the bottom of the modal — admins kept typing
                        addresses, closing the modal, and finding the
                        config reset on next open. */}
                    {categories.length > 0 && (
                      <div className="flex justify-end pt-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => saveRecipientsOnly('categories')}
                          disabled={savingRecipients || savingSched}
                          className="h-8 px-3 text-xs rounded-full border-neutral-300 hover:border-[#0078D4] hover:text-[#0078D4]"
                          data-testid="stock-email-save-cat-recipients"
                        >
                          {savingRecipients ? 'Saving…' : 'Save category lists'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Subject <span className="text-neutral-400 font-normal lowercase tracking-normal">— auto-generated when blank</span>
                </label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder={`Stock Report — ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`}
                  data-testid="stock-email-subject"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Intro note <span className="text-neutral-400 font-normal lowercase tracking-normal">— optional</span>
                </label>
                <textarea
                  value={emailNote}
                  onChange={(e) => setEmailNote(e.target.value)}
                  placeholder="A short message shown above the report…"
                  rows={2}
                  className="w-full px-3 py-2 rounded-md border border-neutral-300 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30 focus:border-[#0078D4]"
                  data-testid="stock-email-note"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Email signature
                </label>
                <Input
                  value={emailSignature}
                  onChange={(e) => setEmailSignature(e.target.value)}
                  placeholder="Warehouse Management"
                  data-testid="stock-email-signature"
                />
                <p className="text-[11px] text-neutral-400 mt-1">Appears below the report as &ldquo;Regards, [signature]&rdquo;.</p>
              </div>

              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 space-y-1">
                <div className="font-bold text-neutral-700 uppercase tracking-wider text-[10px]">What gets sent</div>
                <div>· Columns: <span className="font-medium text-neutral-800">{COLUMNS.filter((c) => visibleCols[c.key]).map((c) => c.label).join(', ')}</span></div>
                <div>· Division: <span className="font-medium text-neutral-800">{catFilter === 'all' ? 'All categories' : (categories.find((c) => c.slug === catFilter)?.name || catFilter)}</span></div>
                <div>· Status filter: <span className="font-medium text-neutral-800">{statusFilter === 'all' ? 'All statuses' : (STATUSES.find((s) => s.value === statusFilter)?.label || statusFilter)}</span></div>
                <label className="flex items-center gap-2 pt-2 mt-1 border-t border-neutral-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeLogo}
                    onChange={(e) => setIncludeLogo(e.target.checked)}
                    className="w-4 h-4 accent-[#0078D4]"
                    data-testid="stock-email-logo-toggle"
                  />
                  <span className="text-sm font-medium text-neutral-800">Show company logo in the header</span>
                </label>
                <div className="text-neutral-400 italic">Adjust the toolbar filters above before sending to change the report contents.</div>
              </div>

              {/* Auto-send frequency: Manual / Daily / Weekly */}
              <div className="rounded-lg border border-neutral-200 p-3">
                <div className="flex items-center gap-2 text-sm font-bold text-neutral-800 mb-2">
                  <Clock className="w-4 h-4 text-[#0078D4]" />
                  Auto-send frequency
                </div>
                <div className="inline-flex w-full rounded-lg border border-neutral-200 bg-neutral-50 p-0.5" data-testid="stock-sched-freq">
                  {[
                    { v: 'manual', label: 'Manual' },
                    { v: 'daily',  label: 'Daily' },
                    { v: 'weekly', label: 'Weekly' },
                  ].map((opt) => {
                    const active = opt.v === 'manual' ? !schedEnabled : (schedEnabled && schedFreq === opt.v);
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => {
                          if (opt.v === 'manual') {
                            setSchedEnabled(false);
                          } else {
                            setSchedEnabled(true);
                            setSchedFreq(opt.v);
                          }
                        }}
                        className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition ${active ? 'bg-white text-[#0078D4] shadow-sm border border-neutral-200' : 'text-neutral-500 hover:text-neutral-800'}`}
                        data-testid={`stock-sched-freq-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {!schedEnabled && (
                  <p className="text-[11px] text-neutral-500 mt-3">
                    Auto-send is off. Use the &ldquo;Send now&rdquo; button to dispatch the report on demand.
                  </p>
                )}

                {/* Trigger-on-upload toggle — independent of the schedule above
                    so admins can pair "no scheduled send" with "send the
                    moment fresh stock arrives". Uses the SAME recipients
                    list configured in this modal. */}
                <label className="mt-3 flex items-start gap-2 cursor-pointer select-none rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 hover:bg-neutral-100">
                  <input
                    type="checkbox"
                    checked={schedAutoSendOnUpload}
                    onChange={(e) => setSchedAutoSendOnUpload(e.target.checked)}
                    className="mt-0.5"
                    data-testid="stock-sched-auto-on-upload"
                  />
                  <span className="text-xs leading-snug">
                    <span className="font-semibold text-neutral-800">Send on every inventory upload</span>
                    <span className="block text-[11px] text-neutral-500 mt-0.5">
                      Trigger this Stock Overview email automatically each time a fresh inventory file is uploaded &amp; committed. Uses the recipients above.
                    </span>
                  </span>
                </label>

                {schedEnabled && (
                  <div className={`mt-3 grid ${schedFreq === 'weekly' ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                    {schedFreq === 'weekly' && (
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Day</label>
                        <select
                          value={schedDow}
                          onChange={(e) => setSchedDow(Number(e.target.value))}
                          className="w-full h-9 px-2 rounded-md border border-neutral-300 bg-white text-sm"
                          data-testid="stock-sched-day"
                        >
                          {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                            <option key={d} value={i}>{d}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Hour (UTC)</label>
                      <select
                        value={schedHour}
                        onChange={(e) => setSchedHour(Number(e.target.value))}
                        className="w-full h-9 px-2 rounded-md border border-neutral-300 bg-white text-sm"
                        data-testid="stock-sched-hour"
                      >
                        {Array.from({ length: 24 }).map((_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>
                        ))}
                      </select>
                    </div>
                    <p className={`text-[11px] text-neutral-500 ${schedFreq === 'weekly' ? 'col-span-2' : ''}`}>
                      Oman is UTC+4 — e.g. 06:00 UTC = 10:00 AM in Muscat.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 px-5 py-4 border-t border-neutral-200 sticky bottom-0 bg-white">
              <Button
                variant="outline"
                onClick={saveSchedule}
                disabled={savingSched}
                className="flex-1"
                data-testid="stock-sched-save"
              >
                <Clock className="w-4 h-4 mr-1" />
                {savingSched
                  ? 'Saving…'
                  : (!schedEnabled
                      ? 'Save (manual only)'
                      : (schedFreq === 'daily' ? 'Save daily schedule' : 'Save weekly schedule'))}
              </Button>
              <Button
                onClick={sendEmailNow}
                disabled={sending}
                className="flex-1 bg-[#0078D4] hover:bg-[#0066B8] text-white"
                data-testid="stock-email-send"
              >
                <Send className="w-4 h-4 mr-1" />
                {sending ? 'Sending…' : 'Send now'}
              </Button>
              <Button
                onClick={sendDigestNow}
                disabled={sendingDigest}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="stock-digest-send"
                title="Sends an analytical weekly insights email with charts and an AI-generated narrative to the same recipients above."
              >
                <BarChart3 className="w-4 h-4 mr-1" />
                {sendingDigest ? 'Generating…' : 'Send Weekly Digest'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Shareable Live Report Modal */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 bg-neutral-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShareOpen(false)}
          data-testid="stock-share-modal"
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-[#0078D4]" />
                <h2 className="text-lg font-bold text-neutral-900">Share Live Stock Report</h2>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                className="p-1.5 rounded-md hover:bg-neutral-100 text-neutral-500"
                data-testid="stock-share-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600 space-y-1.5">
                <div className="font-bold text-neutral-700 uppercase tracking-wider text-[10px]">What this does</div>
                <div>Generates a public URL that renders the current Stock Overview as a live HTML page. Anyone with the link can view it &mdash; no login needed. Refreshing the page shows the latest stock.</div>
              </div>

              {/* Mode switcher — single combined view vs multi vs one link per category */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Link mode</label>
                <div className="inline-flex w-full rounded-lg border border-neutral-200 bg-neutral-50 p-0.5 flex-wrap" data-testid="stock-share-mode">
                  {[
                    { v: 'single', label: 'Single view' },
                    { v: 'multi',  label: 'Multiple categories' },
                    { v: 'per_category', label: 'One link per category' },
                  ].map((opt) => {
                    const active = shareMode === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => { setShareMode(opt.v); setShareUrl(''); setShareShortUrl(''); setShareLinks([]); }}
                        className={`flex-1 min-w-[33%] py-1.5 text-[12px] sm:text-sm font-semibold rounded-md transition ${active ? 'bg-white text-[#0078D4] shadow-sm border border-neutral-200' : 'text-neutral-500 hover:text-neutral-800'}`}
                        data-testid={`stock-share-mode-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Multi-category picker — only when mode === 'multi'. Lets the
                  admin tick exactly which categories share this single link. */}
              {shareMode === 'multi' && (
                <div data-testid="stock-share-multi-cats">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Divisions to include</label>
                    <div className="flex gap-2 text-[11px] font-semibold">
                      <button
                        type="button"
                        onClick={() => {
                          setShareSelectedCats((categories || []).reduce((a, c) => { a[c.slug] = true; return a; }, {}));
                          setShareUrl(''); setShareShortUrl(''); setShareLinks([]);
                        }}
                        className="text-[#0078D4] hover:text-[#0066B8] underline decoration-dotted underline-offset-2"
                        data-testid="stock-share-multi-all"
                      >All</button>
                      <span className="text-neutral-300">·</span>
                      <button
                        type="button"
                        onClick={() => {
                          setShareSelectedCats((categories || []).reduce((a, c) => { a[c.slug] = false; return a; }, {}));
                          setShareUrl(''); setShareShortUrl(''); setShareLinks([]);
                        }}
                        className="text-neutral-500 hover:text-neutral-800 underline decoration-dotted underline-offset-2"
                        data-testid="stock-share-multi-none"
                      >None</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 max-h-48 overflow-y-auto">
                    {(categories || []).map((c) => (
                      <label key={c.slug} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!shareSelectedCats[c.slug]}
                          onChange={(e) => {
                            setShareSelectedCats((prev) => ({ ...prev, [c.slug]: e.target.checked }));
                            setShareUrl(''); setShareShortUrl(''); setShareLinks([]);
                          }}
                          className="w-3.5 h-3.5 accent-[#0078D4]"
                          data-testid={`stock-share-multi-cat-${c.slug}`}
                        />
                        <span className="text-[13px] text-neutral-800 truncate">{c.name || c.slug}</span>
                      </label>
                    ))}
                    {!(categories || []).length && (
                      <div className="text-xs text-neutral-500 italic col-span-full">No divisions available.</div>
                    )}
                  </div>
                  <div className="text-[10px] text-neutral-400 mt-1 italic">
                    {Object.values(shareSelectedCats).filter(Boolean).length} of {(categories || []).length} categories selected · the viewer will see only these.
                  </div>
                </div>
              )}

              {/* Feb 2026 — Warehouses multi-select. Applies to every
                  share mode (single / multi / per-category). Empty
                  selection = all warehouses (backwards-compat). */}
              {shareWarehouses.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Warehouses</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-neutral-200 bg-neutral-50 p-2.5 max-h-48 overflow-y-auto" data-testid="stock-share-warehouses">
                    {shareWarehouses.map((w) => (
                      <label key={w.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!shareSelectedWhs[w.id]}
                          onChange={(e) => {
                            setShareSelectedWhs((prev) => ({ ...prev, [w.id]: e.target.checked }));
                            setShareUrl(''); setShareShortUrl(''); setShareLinks([]);
                          }}
                          className="w-3.5 h-3.5 accent-[#0078D4]"
                          data-testid={`stock-share-warehouse-${w.id}`}
                        />
                        <span className="text-[13px] text-neutral-800 truncate">{w.name || w.id}</span>
                      </label>
                    ))}
                  </div>
                  <div className="text-[10px] text-neutral-400 mt-1 italic">
                    {Object.values(shareSelectedWhs).filter(Boolean).length === 0
                      ? 'No selection = viewer sees stock from all warehouses (default).'
                      : `${Object.values(shareSelectedWhs).filter(Boolean).length} of ${shareWarehouses.length} warehouses selected · the viewer will see only stock in these.`}
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-neutral-200 p-3 text-xs text-neutral-700 space-y-1">
                <div className="font-bold text-neutral-800 uppercase tracking-wider text-[10px] mb-1">Filter baked into {shareMode === 'per_category' ? 'each link' : 'this link'}</div>
                {shareMode === 'single' && (
                  <div>· Division: <span className="font-medium text-neutral-900">{catFilter === 'all' ? 'All categories' : (categories.find((c) => c.slug === catFilter)?.name || catFilter)}</span></div>
                )}
                {shareMode === 'multi' && (
                  <div>· Division: <span className="font-medium text-neutral-900">
                    {(() => {
                      const sel = (categories || []).filter((c) => shareSelectedCats[c.slug]);
                      if (!sel.length) return 'None selected yet';
                      if (sel.length === (categories || []).length) return 'All categories';
                      return sel.map((c) => c.name || c.slug).join(', ');
                    })()}
                  </span></div>
                )}
                {shareMode === 'per_category' && (
                  <div>· Division: <span className="font-medium text-neutral-900">One dedicated link per division in your scope</span></div>
                )}
                <div>· Status: <span className="font-medium text-neutral-900">{statusFilter === 'all' ? 'All statuses' : (STATUSES.find((s) => s.value === statusFilter)?.label || statusFilter)}</span></div>
                <div className="text-neutral-400 italic pt-1">Change category/status filters in the toolbar before opening this modal.</div>
              </div>

              {/* Per-column hide / unhide controls — independent of the admin toolbar so
                  you can publish a slim public view (e.g. Name + Status only) without
                  reconfiguring your own table. `name` and `status` are always rendered
                  server-side even if unticked here. */}
              <div data-testid="stock-share-columns">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500">Columns in this report</label>
                  <div className="flex gap-2 text-[11px] font-semibold">
                    <button
                      type="button"
                      onClick={() => {
                        setShareCols(COLUMNS.reduce((a, c) => { a[c.key] = true; return a; }, {}));
                        setShareUrl(''); setShareLinks([]);
                      }}
                      className="text-[#0078D4] hover:text-[#0066B8] underline decoration-dotted underline-offset-2"
                      data-testid="stock-share-cols-all"
                    >All</button>
                    <span className="text-neutral-300">·</span>
                    <button
                      type="button"
                      onClick={() => {
                        setShareCols(COLUMNS.reduce((a, c) => {
                          a[c.key] = (c.key === 'name' || c.key === 'pack');
                          return a;
                        }, {}));
                        setShareUrl(''); setShareLinks([]);
                      }}
                      className="text-neutral-500 hover:text-neutral-800 underline decoration-dotted underline-offset-2"
                      data-testid="stock-share-cols-min"
                    >Minimal</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5 rounded-lg border border-neutral-200 bg-neutral-50 p-2.5">
                  {COLUMNS.map((c) => {
                    // Feb 2026 — only Product/Name stays locked. Status
                    // used to be forced too, but the report now hides
                    // the Status column entirely and highlights OOS
                    // rows in red instead, so it's toggleable like the
                    // other optional columns.
                    const isForced = c.key === 'name';
                    return (
                      <label
                        key={c.key}
                        className={`flex items-center gap-1.5 ${isForced ? 'opacity-70 cursor-default' : 'cursor-pointer'}`}
                        title={isForced ? 'Always included in the public report' : ''}
                      >
                        <input
                          type="checkbox"
                          checked={isForced ? true : !!shareCols[c.key]}
                          disabled={isForced}
                          onChange={(e) => {
                            setShareCols((prev) => ({ ...prev, [c.key]: e.target.checked }));
                            setShareUrl(''); setShareLinks([]);
                          }}
                          className="w-3.5 h-3.5 accent-[#0078D4]"
                          data-testid={`stock-share-col-${c.key}`}
                        />
                        <span className="text-[13px] text-neutral-800">{c.label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="text-[10px] text-neutral-400 mt-1 italic">Product is always shown &mdash; it&rsquo;s required for the report to make sense.</div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareIncludeLogo}
                  onChange={(e) => { setShareIncludeLogo(e.target.checked); setShareUrl(''); setShareLinks([]); }}
                  className="w-4 h-4 accent-[#0078D4]"
                  data-testid="stock-share-logo"
                />
                <span className="text-sm text-neutral-800">Show company logo in the header</span>
              </label>

              {canStockValue && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shareShowValue}
                    onChange={(e) => { setShareShowValue(e.target.checked); setShareUrl(''); setShareLinks([]); }}
                    className="w-4 h-4 accent-[#0078D4]"
                    data-testid="stock-share-value"
                  />
                  <span className="text-sm text-neutral-800">Show Stock Value KPI</span>
                </label>
              )}

              {/* Super-admin only: Live Report Maintenance toggle.
                  When ON, every visitor (even approved) sees a friendly
                  "Under maintenance" page instead of the data. */}
              {isSuper && (
                <div
                  className={`rounded-lg border p-3 space-y-2.5 ${liveDisabled ? 'border-rose-300 bg-rose-50' : 'border-neutral-200 bg-white'}`}
                  data-testid="live-maintenance-block"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className={`text-[10px] font-bold uppercase tracking-wider ${liveDisabled ? 'text-rose-700' : 'text-neutral-500'}`}>Live report — maintenance mode</div>
                      <div className="text-[11px] text-neutral-600 mt-0.5">
                        {liveDisabled
                          ? 'Live report is PAUSED. Visitors see the maintenance page on every shared link.'
                          : 'Toggle on to pause the live report. Visitors will see a friendly &ldquo;Under maintenance&rdquo; screen.'}
                      </div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={liveDisabled}
                        onChange={(e) => { setLiveDisabled(e.target.checked); saveTickerSettings({ disabled: e.target.checked }); }}
                        className="w-4 h-4 accent-rose-600"
                        data-testid="live-disabled-toggle"
                      />
                      <span className={`text-xs font-semibold ${liveDisabled ? 'text-rose-700' : 'text-neutral-600'}`}>{liveDisabled ? 'Paused' : 'Live'}</span>
                    </label>
                  </div>
                  <div className={liveDisabled ? '' : 'opacity-50'}>
                    <label className="block text-[11px] font-semibold text-neutral-600 mb-1">Maintenance message <span className="text-neutral-400">(optional)</span></label>
                    <textarea
                      value={liveMsg}
                      onChange={(e) => setLiveMsg(e.target.value)}
                      onBlur={() => saveTickerSettings({ maintenance_message: liveMsg })}
                      maxLength={500}
                      rows={2}
                      placeholder="We're updating prices for the new season — back in 30 minutes."
                      className="w-full text-sm px-3 py-2 rounded-md border border-neutral-300 focus:border-rose-500 focus:ring-1 focus:ring-rose-200 outline-none"
                      data-testid="live-maintenance-message"
                    />
                  </div>

                  {/* Daily auto-pause window (Oman time) */}
                  <div className="pt-2 border-t border-neutral-200">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Daily auto-pause window</div>
                        <div className="text-[11px] text-neutral-600 mt-0.5">Automatically show the maintenance page during these hours (Oman time, UTC+4). Wraps over midnight.</div>
                      </div>
                      <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          checked={autoPauseEnabled}
                          onChange={(e) => { setAutoPauseEnabled(e.target.checked); saveTickerSettings({ auto_pause_enabled: e.target.checked }); }}
                          className="w-4 h-4 accent-amber-600"
                          data-testid="auto-pause-toggle"
                        />
                        <span className="text-xs font-semibold text-neutral-700">{autoPauseEnabled ? 'On' : 'Off'}</span>
                      </label>
                    </div>
                    <div className={autoPauseEnabled ? 'mt-2' : 'mt-2 opacity-50 pointer-events-none'}>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">Pause from</label>
                          <input
                            type="time"
                            value={autoPauseStart}
                            onChange={(e) => setAutoPauseStart(e.target.value)}
                            onBlur={() => saveTickerSettings({ auto_pause_start: autoPauseStart })}
                            className="w-full text-sm px-3 py-2 rounded-md border border-neutral-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-200 outline-none font-mono"
                            data-testid="auto-pause-start"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">Resume at</label>
                          <input
                            type="time"
                            value={autoPauseEnd}
                            onChange={(e) => setAutoPauseEnd(e.target.value)}
                            onBlur={() => saveTickerSettings({ auto_pause_end: autoPauseEnd })}
                            className="w-full text-sm px-3 py-2 rounded-md border border-neutral-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-200 outline-none font-mono"
                            data-testid="auto-pause-end"
                          />
                        </div>
                      </div>
                      <div className="text-[10px] text-neutral-500 mt-1.5">
                        Live report will auto-pause every day from <strong className="font-mono">{autoPauseStart}</strong> to <strong className="font-mono">{autoPauseEnd}</strong> (Oman).
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Super-admin only: Live Report Ticker speed control. Affects
                  EVERY shared link's "LIVE UPDATES" marquee on the public report. */}
              {isSuper && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2.5" data-testid="ticker-settings">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Live Updates Ticker</div>
                      <div className="text-[11px] text-amber-800/70 mt-0.5">Marquee that scrolls &ldquo;Glace stock updated today at 12:05 PM&rdquo; on every shared report.</div>
                    </div>
                    <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={tickerEnabled}
                        onChange={(e) => { setTickerEnabled(e.target.checked); saveTickerSettings({ ticker_enabled: e.target.checked }); }}
                        className="w-4 h-4 accent-[#0078D4]"
                        data-testid="ticker-enabled"
                      />
                      <span className="text-xs font-semibold text-amber-900">{tickerEnabled ? 'On' : 'Off'}</span>
                    </label>
                  </div>
                  <div className={tickerEnabled ? '' : 'opacity-50 pointer-events-none'}>
                    <div className="flex items-center justify-between text-[11px] font-semibold text-amber-900 mb-1">
                      <span>Scroll speed</span>
                      <span className="font-mono text-amber-700">
                        {tickerSpeed}s{tickerSpeed >= 60 ? ` (${Math.floor(tickerSpeed / 60)}m${tickerSpeed % 60 ? ` ${tickerSpeed % 60}s` : ''})` : ''} per cycle
                      </span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="600"
                      step="5"
                      value={tickerSpeed}
                      onChange={(e) => setTickerSpeed(Number(e.target.value))}
                      onMouseDown={() => { isAdjustingSpeedRef.current = true; }}
                      onTouchStart={() => { isAdjustingSpeedRef.current = true; }}
                      onKeyDown={() => { isAdjustingSpeedRef.current = true; }}
                      onMouseUp={(e) => { isAdjustingSpeedRef.current = false; saveTickerSettings({ ticker_speed_seconds: Number(e.target.value) }); }}
                      onTouchEnd={(e) => { isAdjustingSpeedRef.current = false; saveTickerSettings({ ticker_speed_seconds: Number(e.target.value) }); }}
                      onKeyUp={(e) => { isAdjustingSpeedRef.current = false; saveTickerSettings({ ticker_speed_seconds: Number(e.target.value) }); }}
                      className="w-full accent-[#0078D4] cursor-pointer"
                      disabled={tickerSaving}
                      data-testid="ticker-speed"
                    />
                    <div className="flex justify-between text-[10px] text-amber-700/70 font-semibold mt-0.5">
                      <span>Fast (10s)</span>
                      <span>Slow (600s / 10 min)</span>
                    </div>
                  </div>
                  {/* Per-item ledger window — how many days of in/out
                      movement each expandable product row shows. */}
                  <div className="pt-2 mt-2 border-t border-amber-200" data-testid="ledger-days-block">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div className="text-[11px] font-semibold text-amber-900">Item ledger window</div>
                        <div className="text-[10px] text-amber-700/80">Tap a product row on the report to see its in/out history + Exp Date</div>
                      </div>
                      <div className="inline-flex rounded-lg border border-amber-300 overflow-hidden">
                        {[10, 15, 20, 30].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => { setLedgerDays(d); saveTickerSettings({ ledger_days: d }); }}
                            className={`px-2.5 py-1 text-[11px] font-bold transition-colors ${ledgerDays === d ? 'bg-[#0078D4] text-white' : 'bg-white text-amber-900 hover:bg-amber-100'}`}
                            data-testid={`ledger-days-${d}`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Label
                  <span className="ml-2 text-[10px] font-normal text-neutral-400 normal-case tracking-normal">(who is this link for?)</span>
                </label>
                <input
                  type="text"
                  value={shareLabel}
                  onChange={(e) => setShareLabel(e.target.value.slice(0, 120))}
                  placeholder="e.g. Glace Distributors — Mubarak"
                  maxLength={120}
                  className="w-full px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm placeholder-neutral-400 focus:border-[#0078D4] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/20 transition"
                  data-testid="stock-share-label-input"
                />
                <div className="mt-1 text-[10px] text-neutral-400">Shown in the audit panel below so you can identify which link goes to whom.</div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Link expires after</label>
                <div className="inline-flex w-full rounded-lg border border-neutral-200 bg-neutral-50 p-0.5" data-testid="stock-share-ttl">
                  {[
                    { v: 1, label: '1 day' },
                    { v: 7, label: '7 days' },
                    { v: 30, label: '30 days' },
                    { v: 0, label: 'Never' },
                  ].map((opt) => {
                    const active = Number(shareTtl) === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => { setShareTtl(opt.v); setShareUrl(''); setShareLinks([]); }}
                        className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition ${active ? 'bg-white text-[#0078D4] shadow-sm border border-neutral-200' : 'text-neutral-500 hover:text-neutral-800'}`}
                        data-testid={`stock-share-ttl-${opt.v}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {shareUrl && (shareMode === 'single' || shareMode === 'multi') && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="stock-share-result">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                      {shareShortUrl ? 'Short live URL · easy to share' : 'Your live URL'}
                    </div>
                    {shareShortUrl && (
                      <button
                        type="button"
                        onClick={() => setShareShowLong((v) => !v)}
                        className="text-[11px] text-emerald-700 hover:text-emerald-900 underline decoration-dotted underline-offset-2"
                        data-testid="stock-share-toggle-long"
                      >
                        {shareShowLong ? 'Hide full URL' : 'Show full URL'}
                      </button>
                    )}
                  </div>
                  <div className="font-mono text-[12px] font-semibold text-neutral-900 break-all bg-white border border-emerald-200 rounded-md p-2 select-all" data-testid="stock-share-short-url">
                    {shareShortUrl || shareUrl}
                  </div>
                  {shareShortUrl && shareShowLong && (
                    <div className="font-mono text-[10px] text-neutral-500 break-all bg-white border border-neutral-200 rounded-md p-2 select-all" data-testid="stock-share-long-url">
                      {shareUrl}
                    </div>
                  )}
                  {shareExpiresAt && (
                    <div className="text-[11px] text-emerald-800">Expires on {formatDateTime(shareExpiresAt)}</div>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <Button
                      variant="outline"
                      onClick={copyShareUrl}
                      className="flex-1"
                      data-testid="stock-share-copy"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {shareCopied ? 'Copied!' : (shareShortUrl ? 'Copy short link' : 'Copy link')}
                    </Button>
                    <a
                      href={shareShortUrl || shareUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-md bg-[#0078D4] hover:bg-[#0066B8] text-white text-sm font-semibold transition"
                      data-testid="stock-share-open"
                    >
                      <ExternalLink className="w-4 h-4" /> Open
                    </a>
                  </div>
                </div>
              )}

              {shareLinks.length > 0 && shareMode === 'per_category' && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-3" data-testid="stock-share-result-per-cat">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{shareLinks.length} live URLs generated</div>
                    <button
                      onClick={copyAllPerCategoryUrls}
                      className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline decoration-dotted underline-offset-2"
                      data-testid="stock-share-copy-all"
                    >
                      {shareCopiedSlug === '__all__' ? 'Copied all!' : 'Copy all'}
                    </button>
                  </div>
                  {shareExpiresAt && (
                    <div className="text-[11px] text-emerald-800">Expire on {formatDateTime(shareExpiresAt)}</div>
                  )}
                  <div className="space-y-2">
                    {shareLinks.map((l) => {
                      const displayUrl = l.short_url || l.url;
                      return (
                      <div
                        key={l.category_slug}
                        className="rounded-md bg-white border border-emerald-200 p-2.5 space-y-1.5"
                        data-testid={`stock-share-cat-row-${l.category_slug}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm text-neutral-900 truncate">{l.category_name}</div>
                          <span className="text-[10px] font-mono text-neutral-400">/{l.category_slug}</span>
                        </div>
                        <div className="font-mono text-[11px] font-semibold text-neutral-900 break-all select-all leading-snug" data-testid={`stock-share-cat-shorturl-${l.category_slug}`}>{displayUrl}</div>
                        <div className="flex gap-2 pt-0.5">
                          <button
                            onClick={() => copyPerCategoryUrl(l.category_slug, displayUrl)}
                            className="flex-1 inline-flex items-center justify-center gap-1 h-8 px-2 rounded-md border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-xs font-semibold transition"
                            data-testid={`stock-share-cat-copy-${l.category_slug}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                            {shareCopiedSlug === l.category_slug ? 'Copied!' : 'Copy'}
                          </button>
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 inline-flex items-center justify-center gap-1 h-8 px-2 rounded-md bg-[#0078D4] hover:bg-[#0066B8] text-white text-xs font-semibold transition"
                            data-testid={`stock-share-cat-open-${l.category_slug}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> Open
                          </a>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ---- Generated URL records (audit / revoke / delete) ---- */}
              <div className="rounded-lg border border-neutral-200 bg-white p-3 space-y-3" data-testid="stock-share-records-panel">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Generated URLs</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">Every URL minted from this screen. Revoke disables the link immediately (returns 410); Delete removes the record entirely.</div>
                  </div>
                  <button
                    type="button"
                    onClick={loadShareRecords}
                    disabled={shareRecordsLoading}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-[11px] font-semibold text-neutral-700 transition disabled:opacity-50"
                    data-testid="stock-share-records-refresh"
                    title="Refresh"
                  >
                    <RotateCw className={`w-3.5 h-3.5 ${shareRecordsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {[
                    { v: 'all',     label: 'All' },
                    { v: 'active',  label: 'Active' },
                    { v: 'revoked', label: 'Revoked' },
                    { v: 'expired', label: 'Expired' },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setShareRecordsStatusFilter(opt.v)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                        shareRecordsStatusFilter === opt.v
                          ? 'bg-[#0078D4] text-white border-[#0078D4]'
                          : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50'
                      }`}
                      data-testid={`stock-share-records-filter-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {(() => {
                  const filtered = (shareRecords || []).filter((r) => shareRecordsStatusFilter === 'all' ? true : r.status === shareRecordsStatusFilter);
                  if (shareRecordsLoading && filtered.length === 0) {
                    return <div className="text-[11px] text-neutral-400 italic py-2">Loading…</div>;
                  }
                  if (filtered.length === 0) {
                    return <div className="text-[11px] text-neutral-400 italic py-2" data-testid="stock-share-records-empty">No URLs in this view yet.</div>;
                  }
                  return (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="stock-share-records-list">
                      {filtered.map((r) => {
                        const statusClass = r.status === 'active'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : r.status === 'revoked'
                            ? 'bg-rose-50 text-rose-700 border-rose-200'
                            : 'bg-neutral-100 text-neutral-500 border-neutral-200';
                        const created = formatDateTime(r.created_at) || '—';
                        const expires = formatDate(r.expires_at) || 'No expiry';
                        const busy = shareRecordBusySlug === r.slug;
                        return (
                          <div
                            key={r.slug}
                            className="rounded-md border border-neutral-200 bg-neutral-50 p-2 flex flex-col sm:flex-row sm:items-center gap-2"
                            data-testid={`stock-share-record-row-${r.slug}`}
                          >
                            <div className="flex-1 min-w-0">
                              {/* Label takes top billing — answers "who is this link for?" at a glance. Click to edit inline. */}
                              {shareRecordEditingSlug === r.slug ? (
                                <div className="flex items-center gap-1.5">
                                  <input
                                    type="text"
                                    autoFocus
                                    value={shareRecordEditValue}
                                    onChange={(e) => setShareRecordEditValue(e.target.value.slice(0, 120))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveShareRecordLabel(r.slug, shareRecordEditValue);
                                      if (e.key === 'Escape') { setShareRecordEditingSlug(''); setShareRecordEditValue(''); }
                                    }}
                                    placeholder="e.g. Glace Distributors — Mubarak"
                                    maxLength={120}
                                    className="flex-1 min-w-0 px-2 py-1 rounded border border-[#0078D4] bg-white text-[12px] focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30"
                                    data-testid={`stock-share-record-label-input-${r.slug}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => saveShareRecordLabel(r.slug, shareRecordEditValue)}
                                    disabled={shareRecordBusySlug === r.slug}
                                    className="px-2 py-1 rounded text-[11px] font-semibold bg-[#0078D4] text-white hover:bg-[#0067be] transition disabled:opacity-50"
                                    data-testid={`stock-share-record-label-save-${r.slug}`}
                                  >Save</button>
                                  <button
                                    type="button"
                                    onClick={() => { setShareRecordEditingSlug(''); setShareRecordEditValue(''); }}
                                    className="px-2 py-1 rounded text-[11px] font-semibold text-neutral-600 hover:bg-neutral-100 transition"
                                  >Cancel</button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => { setShareRecordEditingSlug(r.slug); setShareRecordEditValue(r.label || ''); }}
                                  className="group text-[12px] font-semibold text-neutral-900 truncate text-left hover:text-[#0078D4] transition flex items-center gap-1.5"
                                  title={r.label ? `Click to edit: ${r.label}` : 'Click to add label'}
                                  data-testid={`stock-share-record-label-${r.slug}`}
                                >
                                  {r.label || <span className="text-neutral-400 italic font-normal">+ Add label</span>}
                                  <span className="opacity-0 group-hover:opacity-100 transition text-[10px] text-[#0078D4]">edit</span>
                                </button>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border ${statusClass}`}>
                                  {r.status}
                                </span>
                                {shareRecordEditingSlugFor === r.slug ? (
                                  <div className="w-full space-y-1" data-testid={`stock-share-record-slug-editor-${r.slug}`}>
                                    <div className="flex items-center gap-1 min-w-0">
                                      <span className="font-mono text-[10px] text-neutral-400 whitespace-nowrap">/api/s/</span>
                                      <input
                                        type="text"
                                        autoFocus
                                        value={shareRecordSlugValue}
                                        onChange={(e) => setShareRecordSlugValue(
                                          e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, '').slice(0, 60)
                                        )}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') saveShareRecordSlug(r.slug, shareRecordSlugValue);
                                          if (e.key === 'Escape') { setShareRecordEditingSlugFor(''); setShareRecordSlugValue(''); }
                                        }}
                                        placeholder="e.g. glace or warehouse-team"
                                        maxLength={60}
                                        className="flex-1 min-w-0 px-1.5 py-1 rounded border border-[#0078D4] bg-white text-[12px] font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]/30"
                                        data-testid={`stock-share-record-slug-input-${r.slug}`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => saveShareRecordSlug(r.slug, shareRecordSlugValue)}
                                        disabled={busy}
                                        className="px-2 py-1 rounded text-[11px] font-semibold bg-[#0078D4] text-white hover:bg-[#0067be] transition disabled:opacity-50"
                                        data-testid={`stock-share-record-slug-save-${r.slug}`}
                                      >Save</button>
                                      <button
                                        type="button"
                                        onClick={() => { setShareRecordEditingSlugFor(''); setShareRecordSlugValue(''); }}
                                        className="px-2 py-1 rounded text-[11px] font-semibold text-neutral-600 hover:bg-neutral-100 transition"
                                      >Cancel</button>
                                    </div>
                                    {/* Live preview of the final URL as you type */}
                                    <div className="text-[10px] font-mono text-emerald-700 truncate" data-testid={`stock-share-record-slug-preview-${r.slug}`}>
                                      → {String(r.short_url || '').replace(/\/[^/]*$/, '')}/{shareRecordSlugValue || '…'}
                                    </div>
                                    {(() => {
                                      const slugify = (s) => String(s || '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
                                      const sugg = [];
                                      const fromLabel = slugify(r.label);
                                      if (fromLabel.length >= 3) sugg.push(fromLabel);
                                      const fromCat = slugify(r.category_slug);
                                      if (fromCat.length >= 3 && !sugg.includes(fromCat)) sugg.push(fromCat);
                                      if (!sugg.length) return null;
                                      return (
                                        <div className="flex items-center gap-1 flex-wrap">
                                          <span className="text-[9px] uppercase font-bold text-neutral-400">Tap to use:</span>
                                          {sugg.map((s) => (
                                            <button
                                              key={s}
                                              type="button"
                                              onClick={() => setShareRecordSlugValue(s)}
                                              className="px-1.5 py-0.5 rounded-full bg-neutral-100 hover:bg-[#0078D4]/10 text-[10px] font-mono text-neutral-700 hover:text-[#0078D4] transition"
                                              data-testid={`stock-share-record-slug-suggestion-${r.slug}-${s}`}
                                            >{s}</button>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <>
                                    <span className="font-mono text-[10px] text-neutral-500 truncate" title={r.short_url}>{r.short_url}</span>
                                    <button
                                      type="button"
                                      onClick={() => { setShareRecordEditingSlugFor(r.slug); setShareRecordSlugValue(r.slug); }}
                                      className="inline-flex items-center justify-center w-5 h-5 rounded text-neutral-400 hover:text-[#0078D4] hover:bg-[#0078D4]/10 transition"
                                      title="Edit URL slug (rename after /api/s/)"
                                      data-testid={`stock-share-record-slug-edit-${r.slug}`}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  </>
                                )}
                                {r.category_slug && (
                                  <span className="text-[10px] font-mono text-neutral-500">/{r.category_slug}</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => openShareRecordColumns(r)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-[#0078D4]/10 text-[#0078D4] hover:bg-[#0078D4]/20 transition"
                                  title="Show/hide columns on this share link"
                                  data-testid={`stock-share-record-columns-edit-${r.slug}`}
                                >
                                  <Pencil className="w-2.5 h-2.5" />
                                  {(r.columns && r.columns.length) ? `${r.columns.length} col${r.columns.length === 1 ? '' : 's'}` : 'columns'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openShareRecordCats(r)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-emerald-600/10 text-emerald-700 hover:bg-emerald-600/20 transition"
                                  title="Edit which divisions this share link shows"
                                  data-testid={`stock-share-record-cats-edit-${r.slug}`}
                                >
                                  <Pencil className="w-2.5 h-2.5" />
                                  {(r.category_slugs && r.category_slugs.length) ? `${r.category_slugs.length} cat${r.category_slugs.length === 1 ? '' : 's'}` : 'all cats'}{r.status_filter ? ` · ${r.status_filter === 'in_stock' ? 'in only' : r.status_filter === 'out_of_stock' ? 'out only' : 'soon only'}` : ''}
                                </button>
                              </div>
                              <div className="text-[10px] text-neutral-500 mt-0.5 flex flex-wrap gap-x-2">
                                <span>Created {created}</span>
                                <span>· Expires {expires}</span>
                                <span>· {r.hits} hit{r.hits === 1 ? '' : 's'}</span>
                                {r.created_by && <span>· by {r.created_by}</span>}
                              </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(r.short_url); toast({ title: 'Copied' }); }}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-600 transition"
                                title="Copy"
                                data-testid={`stock-share-record-copy-${r.slug}`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <a
                                href={r.short_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-600 transition"
                                title="Open"
                                data-testid={`stock-share-record-open-${r.slug}`}
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                              {(r.status === 'active' || r.status === 'expired') && (
                                shareRecordExtendMenuFor === r.slug ? (
                                  <div className="inline-flex items-center gap-1 px-1 py-0.5 rounded-md border border-[#0078D4] bg-[#0078D4]/5" data-testid={`stock-share-record-extend-menu-${r.slug}`}>
                                    {[7, 30, 90].map((d) => (
                                      <button
                                        key={d}
                                        type="button"
                                        onClick={() => { extendShareRecordExpiry(r.slug, d); setShareRecordExtendMenuFor(''); }}
                                        disabled={busy}
                                        className="px-1.5 py-0.5 rounded text-[10px] font-bold text-[#0078D4] hover:bg-[#0078D4] hover:text-white transition disabled:opacity-50"
                                        data-testid={`stock-share-record-extend-${d}d-${r.slug}`}
                                      >+{d}d</button>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={() => setShareRecordExtendMenuFor('')}
                                      className="px-1 py-0.5 rounded text-[10px] text-neutral-500 hover:bg-neutral-100 transition"
                                    ><X className="w-3 h-3" /></button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setShareRecordExtendMenuFor(r.slug)}
                                    disabled={busy}
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-md border transition disabled:opacity-50 ${r.status === 'expired' ? 'border-orange-200 bg-orange-50 hover:bg-orange-100 text-orange-700' : 'border-blue-200 bg-blue-50 hover:bg-blue-100 text-[#0078D4]'}`}
                                    title={r.status === 'expired' ? 'Revive & extend expiry' : 'Extend expiry (+7 / +30 / +90 days)'}
                                    data-testid={`stock-share-record-extend-${r.slug}`}
                                  >
                                    <Clock className="w-3.5 h-3.5" />
                                  </button>
                                )
                              )}
                              {r.status === 'active' && (
                                <button
                                  type="button"
                                  onClick={() => revokeShareRecord(r.slug)}
                                  disabled={busy}
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 transition disabled:opacity-50"
                                  title="Revoke (kills both short URL and long signed URL)"
                                  data-testid={`stock-share-record-revoke-${r.slug}`}
                                >
                                  <Ban className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {r.status === 'revoked' && (
                                <button
                                  type="button"
                                  onClick={() => restoreShareRecord(r.slug)}
                                  disabled={busy}
                                  className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition disabled:opacity-50"
                                  title="Restore (re-enable both URLs)"
                                  data-testid={`stock-share-record-restore-${r.slug}`}
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => deleteShareRecord(r.slug)}
                                disabled={busy}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 transition disabled:opacity-50"
                                title="Delete"
                                data-testid={`stock-share-record-delete-${r.slug}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 px-5 py-4 border-t border-neutral-200 sticky bottom-0 bg-white">
              <Button
                variant="outline"
                onClick={() => setShareOpen(false)}
                className="flex-1"
                data-testid="stock-share-cancel"
              >
                Close
              </Button>
              <Button
                onClick={generateShareUrl}
                disabled={shareGenerating}
                className="flex-1 bg-[#0078D4] hover:bg-[#0066B8] text-white"
                data-testid="stock-share-generate"
              >
                <Link2 className="w-4 h-4 mr-1" />
                {shareGenerating
                  ? 'Generating…'
                  : ((shareUrl || shareLinks.length > 0)
                      ? 'Regenerate'
                      : (shareMode === 'per_category' ? 'Generate links' : 'Generate link'))}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit columns modal — re-signs the underlying token so the
          same slug now serves a different column set. */}
      {shareRecordColumnsModalFor && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-3 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShareRecordColumnsModalFor(''); }}
          data-testid="stock-share-columns-modal"
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#0078D4]">Share link</div>
                <div className="text-sm font-semibold text-neutral-900 font-mono">/api/s/{shareRecordColumnsModalFor}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Toggle which columns this link shows on the public report.</div>
              </div>
              <button
                type="button"
                onClick={() => setShareRecordColumnsModalFor('')}
                className="p-1.5 rounded-md hover:bg-neutral-100 transition"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                {COLUMNS.map((c) => {
                  const required = c.required;
                  const checked = required ? true : !!shareRecordColumnsDraft[c.key];
                  return (
                    <label
                      key={c.key}
                      className={`flex items-center gap-2 text-[13px] ${required ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:text-[#0078D4]'}`}
                      data-testid={`stock-share-columns-toggle-${c.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={required}
                        onChange={(e) => setShareRecordColumnsDraft((prev) => ({ ...prev, [c.key]: e.target.checked }))}
                        className="w-4 h-4 rounded border-neutral-300 text-[#0078D4] focus:ring-[#0078D4]"
                      />
                      <span className="font-medium">{c.label}</span>
                      {required && <span className="text-[9px] uppercase tracking-wide text-neutral-400">required</span>}
                    </label>
                  );
                })}
              </div>
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5 mt-4">
                Saving re-signs the underlying token. The short URL (<span className="font-mono">/api/s/{shareRecordColumnsModalFor}</span>) keeps working. Any bookmarked <em>long</em> signed URL from before this save will stop working.
              </div>
            </div>
            <div className="flex gap-2 px-5 py-3 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={() => setShareRecordColumnsModalFor('')}
                className="flex-1"
                data-testid="stock-share-columns-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={saveShareRecordColumns}
                disabled={shareRecordBusySlug === shareRecordColumnsModalFor}
                className="flex-1 bg-[#0078D4] hover:bg-[#0066B8] text-white"
                data-testid="stock-share-columns-save"
              >
                {shareRecordBusySlug === shareRecordColumnsModalFor ? 'Saving…' : 'Save columns'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Edit categories modal — re-signs the underlying token so the
          same slug now shows a different category set. */}
      {shareRecordCatsModalFor && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-3 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShareRecordCatsModalFor(''); }}
          data-testid="stock-share-cats-modal"
        >
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Share link</div>
                <div className="text-sm font-semibold text-neutral-900 font-mono">/api/s/{shareRecordCatsModalFor}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">Choose which divisions this link shows on the public report.</div>
              </div>
              <button
                type="button"
                onClick={() => setShareRecordCatsModalFor('')}
                className="p-1.5 rounded-md hover:bg-neutral-100 transition"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <label
                className="flex items-center gap-2 text-[13px] font-semibold cursor-pointer pb-3 mb-3 border-b border-neutral-100"
                data-testid="stock-share-cats-toggle-all"
              >
                <input
                  type="checkbox"
                  checked={shareRecordCatsAll}
                  onChange={(e) => setShareRecordCatsAll(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-600"
                />
                <span>All divisions</span>
                <span className="text-[10px] font-normal text-neutral-400">(entire stock report)</span>
              </label>
              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 ${shareRecordCatsAll ? 'opacity-40 pointer-events-none' : ''}`}>
                {(categories || []).map((c) => (
                  <label
                    key={c.slug}
                    className="flex items-center gap-2 text-[13px] cursor-pointer hover:text-emerald-700"
                    data-testid={`stock-share-cats-toggle-${c.slug}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!shareRecordCatsDraft[c.slug]}
                      onChange={(e) => setShareRecordCatsDraft((prev) => ({ ...prev, [c.slug]: e.target.checked }))}
                      className="w-4 h-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-600"
                    />
                    <span className="font-medium truncate">{c.name}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-neutral-100">
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Items shown</div>
                <select
                  value={shareRecordStatusDraft}
                  onChange={(e) => setShareRecordStatusDraft(e.target.value)}
                  className="w-full px-2.5 py-2 rounded-md border border-neutral-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-600/30"
                  data-testid="stock-share-cats-status-select"
                >
                  <option value="">All items (in + out of stock + coming soon)</option>
                  <option value="in_stock">In stock only</option>
                  <option value="out_of_stock">Out of stock only</option>
                  <option value="coming_soon">Coming soon only</option>
                </select>
                {shareRecordStatusDraft === 'in_stock' && (
                  <div className="text-[11px] text-red-700 mt-1.5" data-testid="stock-share-cats-status-warning">
                    ⚠ Out-of-stock items will be hidden from this link&apos;s report table.
                  </div>
                )}
              </div>
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5 mt-4">
                Saving re-signs the underlying token. The short URL (<span className="font-mono">/api/s/{shareRecordCatsModalFor}</span>) keeps working and viewers will see the new category set on their next visit. Any bookmarked <em>long</em> signed URL from before this save will stop working.
              </div>
            </div>
            <div className="flex gap-2 px-5 py-3 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={() => setShareRecordCatsModalFor('')}
                className="flex-1"
                data-testid="stock-share-cats-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={saveShareRecordCats}
                disabled={shareRecordBusySlug === shareRecordCatsModalFor}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="stock-share-cats-save"
              >
                {shareRecordBusySlug === shareRecordCatsModalFor ? 'Saving…' : 'Save categories'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminStock;
